import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import {
  Scissors,
  Sparkles,
  Palette,
  Eye,
  Calendar as CalendarIcon,
  Utensils,
  ChevronRight,
  ChevronLeft,
  PartyPopper,
  GraduationCap,
  Heart,
  Users,
  Crown,
  Cake,
  Star,
  Check,
  ShoppingBag,
  TrendingUp,
  Presentation,
  Building,
  Loader2,
  Clock,
  User,
  Search,
  Edit3,
  Share,
  Camera,
  Image as ImageIcon,
  ChevronDown,
  X,
  Plus,
  Mail,
  Phone,
  Package,
  RefreshCw,
  Plane,
  Music,
  Tag,
  MapPin,
} from "lucide-react";
import { useExperienceCategories, ICON_MAP } from "@/hooks/useExperienceCategories";
import { ImagePicker } from "@/components/shared/ImagePicker";
import { getCategoryPreset } from "@/lib/categoryConfig";
import { useLiveCategoryMeta } from "@/hooks/useLiveCategoryMeta";
import BrandPicker from "@/components/host/BrandPicker";
import PreferenceSchemaInline from "@/components/PreferenceSchemaInline";
import GooglePlacesAutocomplete from "@/components/GooglePlacesAutocomplete";
import { useUiSurface, slotText } from "@/hooks/useUiSurface";
import { Slot } from "@/components/cms/Slot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IconComponents = {
  Scissors, Sparkles, Palette, Eye, Utensils, PartyPopper, GraduationCap, Heart,
  Users, Cake, Star, ShoppingBag, TrendingUp, Presentation, Building, CalendarIcon,
  RefreshCw, Plane, Music, Camera
};

const getIconComponent = (iconName, categoryId) => {
  // Prefer canonical category icon (image-based) when category is known
  const preset = categoryId ? getCategoryPreset(String(categoryId).replace(/^cat_/, "")) : null;
  if (preset?.icon) return preset.icon;
  const componentName = ICON_MAP[iconName] || ICON_MAP.default;
  return IconComponents[componentName] || Sparkles;
};

// Resolve canonical solid hex from a category id (admin-controlled, single source).
// Iter 9.35: reads from live db.categories first (via useLiveCategoryMeta cache), then static preset.
const getCategoryHex = (categoryId, fallback = "#6B7280") => {
  if (!categoryId) return fallback;
  const cid = String(categoryId).replace(/^cat_/, "");
  // Live cache lookup (module-level cache shared across components)
  try {
    const live = (typeof window !== "undefined" && window.__LIVE_CAT_META__) ? window.__LIVE_CAT_META__[cid] : null;
    if (live?.dark_hex) return live.dark_hex;
  } catch (_) { /* silent */ }
  const preset = getCategoryPreset(cid);
  return preset?.iconColor || fallback;
};

// Mirror the useLiveCategoryMeta hook's cache to window for non-React helpers above.
export function _SyncLiveHexCache() {
  const { meta } = useLiveCategoryMeta();
  if (typeof window !== "undefined") {
    window.__LIVE_CAT_META__ = meta;
  }
  return null;
}

// Convert a hex string to a soft 12% alpha bg for tile backgrounds (no gradient).
const hexToSoftBg = (hex) => `${hex}1F`; // ~12% alpha

// Iter 9.47.u - Occasion picker that combines Phygital templates + user's own
// existing occasions + a "create new" path. Replaces the old <select> dropdown.
function OccasionPickerInline({ category, userOccasions = [], value, onChange }) {
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!category) return;
    const c = String(category).replace(/^cat_/, "").toLowerCase();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/occasion-templates?category=${c}`)
      .then((r) => r.ok ? r.json() : { templates: [] })
      .then((d) => setTemplates(d.templates || []))
      .catch(() => setTemplates([]));
  }, [category]);

  const selected = (() => {
    if (!value) return null;
    const t = templates.find((x) => x.occasion_id === value);
    if (t) return { id: t.occasion_id, name: t.name, kind: "template" };
    const u = userOccasions.find((x) => x.experience_id === value);
    if (u) return { id: u.experience_id, name: u.name, kind: "user" };
    return null;
  })();

  const lower = search.trim().toLowerCase();
  const filteredTemplates = templates.filter(
    (t) => !lower || t.name.toLowerCase().includes(lower),
  );
  const filteredUser = userOccasions.filter(
    (u) => !lower || (u.name || "").toLowerCase().includes(lower),
  );
  const showCreate = lower.length >= 2 && !filteredTemplates.find((t) => t.name.toLowerCase() === lower) && !filteredUser.find((u) => (u.name || "").toLowerCase() === lower);

  return (
    <div data-testid="occasion-picker">
      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-2">
        <PartyPopper className="w-3.5 h-3.5 inline mr-1" />
        Link to occasion (optional)
      </label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm bg-white text-left flex items-center justify-between hover:border-gray-400 transition"
        data-testid="occasion-picker-trigger"
      >
        <span className={selected ? "text-gray-900" : "text-gray-400"}>
          {selected ? selected.name : "Search or pick an occasion..."}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1 rounded-lg border border-gray-200 bg-white shadow-sm max-h-72 overflow-y-auto">
          <div className="p-2 sticky top-0 bg-white border-b border-gray-100">
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search occasions..."
              className="h-8 text-xs"
              data-testid="occasion-picker-search"
            />
          </div>
          {value && (
            <button
              type="button"
              onClick={() => { onChange?.(null); setOpen(false); }}
              className="w-full px-3 py-2 text-xs text-left text-gray-500 hover:bg-gray-50"
              data-testid="occasion-picker-clear"
            >
              Clear selection
            </button>
          )}
          {filteredUser.length > 0 && (
            <div>
              <p className="px-3 pt-2 text-[10px] uppercase font-semibold tracking-wide text-gray-400">Your occasions</p>
              {filteredUser.map((u) => (
                <button
                  key={u.experience_id}
                  type="button"
                  onClick={() => { onChange?.(u.experience_id); setOpen(false); }}
                  className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-50 ${value === u.experience_id ? "bg-purple-50" : ""}`}
                  data-testid={`occasion-picker-user-${u.experience_id}`}
                >
                  {u.name}
                </button>
              ))}
            </div>
          )}
          {filteredTemplates.length > 0 && (
            <div>
              <p className="px-3 pt-2 text-[10px] uppercase font-semibold tracking-wide text-gray-400">Phygital occasion templates</p>
              {filteredTemplates.map((t) => (
                <button
                  key={t.occasion_id}
                  type="button"
                  onClick={() => { onChange?.(t.occasion_id); setOpen(false); }}
                  className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-50 ${value === t.occasion_id ? "bg-purple-50" : ""}`}
                  data-testid={`occasion-picker-template-${t.occasion_id}`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          )}
          {showCreate && (
            <button
              type="button"
              onClick={() => {
                // Pass a sentinel so the parent can spawn a "create occasion" flow later.
                // For now we set a free-text id prefixed `new:` which the form can persist as a custom occasion name.
                onChange?.(`new:${search.trim()}`);
                setOpen(false);
              }}
              className="w-full px-3 py-2 text-sm text-left text-purple-700 hover:bg-purple-50 border-t border-gray-100"
              data-testid="occasion-picker-create-new"
            >
              <Plus className="w-3 h-3 inline mr-1" />
              Create new: <span className="font-medium">{search.trim()}</span>
            </button>
          )}
          {filteredTemplates.length === 0 && filteredUser.length === 0 && !showCreate && (
            <p className="px-3 py-4 text-xs text-gray-400 text-center">No matches. Type to create a new one.</p>
          )}
        </div>
      )}
    </div>
  );
}

// Iter 9.47.q - Preferences trigger button + nested popup editor.
// Avoids bloating the main plan modal by hiding the long preference list behind
// an "Edit preferences" button that summarises selected count.
function PreferencePopupTrigger({ categoryId, value = {}, onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || {});
  // Re-sync draft if external value changes (e.g. clearing on category switch)
  useEffect(() => { setDraft(value || {}); }, [categoryId]);

  const totalSelected = Object.values(value || {}).reduce((acc, v) => {
    if (Array.isArray(v)) return acc + v.length;
    if (typeof v === "string" && v.length) return acc + 1;
    return acc;
  }, 0);

  const summary = totalSelected === 0
    ? "No preferences set yet"
    : `${totalSelected} preference${totalSelected === 1 ? "" : "s"} selected`;

  return (
    <>
      <button
        type="button"
        onClick={() => { setDraft(value || {}); setOpen(true); }}
        className="w-full flex items-center justify-between gap-2 rounded-lg border border-stone-200 bg-stone-50 hover:bg-stone-100 px-3 py-2 text-left transition"
        data-testid="plan-preferences-edit-btn"
      >
        <span className="flex items-center gap-2 min-w-0">
          <span className={`text-xs font-medium ${totalSelected > 0 ? "text-stone-900" : "text-stone-500"} truncate`}>
            {summary}
          </span>
        </span>
        <span className="text-[10px] text-stone-500 shrink-0 inline-flex items-center gap-1">
          <Edit3 className="w-3 h-3" />
          Edit preferences
        </span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-lg max-h-[85vh] overflow-y-auto"
          data-testid="plan-preferences-popup"
        >
          <DialogHeader>
            <DialogTitle data-testid="plan-preferences-popup-title">Edit preferences</DialogTitle>
          </DialogHeader>
          <div className="pt-2">
            <PreferenceSchemaInline
              categoryId={categoryId}
              value={draft}
              onChange={setDraft}
              testidPrefix="plan-pref"
            />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t mt-3 sticky bottom-0 bg-white">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              data-testid="plan-preferences-popup-cancel"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => { onChange?.(draft); setOpen(false); }}
              data-testid="plan-preferences-popup-save"
              className="bg-stone-900 hover:bg-stone-800 text-white"
            >
              Save preferences
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


/**
 * InspoPopupTrigger - matches the PreferencePopupTrigger pattern.
 * Shows a card with "No inspo set yet" or "N images selected" + a pencil.
 * Opens a Dialog wrapping the standard ImagePicker, scoped to the
 * selected category (e.g. makeup → My Makeup Inspo folder).
 */
function InspoPopupTrigger({ categoryId, selectedInspo = [], userInspo = [], onChange, sectionLabel = "Inspo" }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(selectedInspo || []);
  useEffect(() => { setDraft(selectedInspo || []); }, [categoryId]);

  const count = (selectedInspo || []).length;
  const summary = count === 0
    ? "No inspo set yet"
    : `${count} image${count === 1 ? "" : "s"} selected`;

  // Translate stored ids back into urls so ImagePicker shows the previews.
  const selectedUrls = (selectedInspo || []).map((id) => {
    const found = (userInspo || []).find((i) => (i.inspo_id || i.id) === id);
    return found?.image_url || found?.url || id;
  }).filter(Boolean);

  const draftUrls = (draft || []).map((id) => {
    const found = (userInspo || []).find((i) => (i.inspo_id || i.id) === id);
    return found?.image_url || found?.url || id;
  }).filter(Boolean);

  return (
    <>
      <button
        type="button"
        onClick={() => { setDraft(selectedInspo || []); setOpen(true); }}
        className="w-full flex items-center justify-between gap-2 rounded-lg border border-stone-200 bg-stone-50 hover:bg-stone-100 px-3 py-2 text-left transition"
        data-testid="plan-inspo-edit-btn"
      >
        <span className="flex items-center gap-2 min-w-0">
          <Camera className="w-3.5 h-3.5 text-stone-500 shrink-0" />
          <span className={`text-xs font-medium ${count > 0 ? "text-stone-900" : "text-stone-500"} truncate`}>
            {summary}
          </span>
          {count > 0 && (
            <span className="flex -space-x-1.5 shrink-0">
              {selectedUrls.slice(0, 3).map((u, i) => (
                <img key={i} src={u} alt="" className="w-6 h-6 rounded-md object-cover ring-1 ring-white" />
              ))}
            </span>
          )}
        </span>
        <span className="text-[10px] text-stone-500 shrink-0 inline-flex items-center gap-1">
          <Edit3 className="w-3 h-3" />
          Edit inspo
        </span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-lg max-h-[85vh] overflow-y-auto"
          data-testid="plan-inspo-popup"
        >
          <DialogHeader>
            <DialogTitle data-testid="plan-inspo-popup-title">{sectionLabel || "Edit inspo"}</DialogTitle>
            <DialogDescription className="text-xs text-stone-500">
              You can always add more inspo later from your profile.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <ImagePicker
              selectedImages={draftUrls}
              onSelect={setDraft}
              category={categoryId}
              maxImages={8}
              compact
            />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t mt-3 sticky bottom-0 bg-white">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              data-testid="plan-inspo-popup-cancel"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => { onChange?.(draft); setOpen(false); }}
              data-testid="plan-inspo-popup-save"
              className="bg-stone-900 hover:bg-stone-800 text-white"
            >
              Save inspo
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
// Backend returns singular IDs (nail, lash) - matches both singular and plural fallthrough.
// Note: backend also has "style" as a duplicate entry to "style"; we hide "style" and
// surface "style" with label "Style" so users see Style as a top-level option without
// dual-entry confusion.
// Iter 9.54 - This whitelist is a SAFETY FALLBACK only. The picker primarily
// gates via /api/launch-control/live-categories, which is Airtable-driven.
// Adding a new category in Airtable will surface automatically without touching
// this list, so long as launch-control is reachable. Kept here for offline safety
// and as a hard-block for legacy Mongo categories we don't want to expose.
export const V1_LAUNCHED_CATEGORIES = new Set([
  "hair", "makeup", "nail", "nails", "lash", "lashes", "culinary", "style", "routine"
]);
// Hidden because they duplicate or aren't V1: style (=retail), wellness, travel, dispensary, events, instructor

// User-facing label overrides for V1 (e.g. "Food" instead of "Culinary", "Style" instead of "Retail")
// Iter 9.47.l - added 'style' key alongside 'retail' because the canonical Retail
// category in the API has id='style'; previously the override never matched and
// the tile rendered the raw cat.name ('Retail').
export const V1_LABEL_OVERRIDES = {
  culinary: "Food",
  retail: "Style",
  style: "Style",
  nail: "Nails",
  lash: "Lashes",
};

const isV1Category = (cat) => {
  const id = String(cat?.id || "").replace(/^cat_/, "").toLowerCase();
  // Iter 9.47.l - Style/Retail is now V1-eligible so users can plan style
  // experiences end-to-end (Brand & link / My sizes blocks live in the modal).
  // The legacy duplicate "Events" record that mistakenly shares id='style'
  // is filtered out by name below - the canonical Style entry uses name 'Retail'.
  if (id === "style") {
    const name = String(cat?.name || "").toLowerCase();
    return name !== "events";
  }
  return V1_LAUNCHED_CATEGORIES.has(id);
};

const v1Label = (cat) => {
  const id = String(cat?.id || "").replace(/^cat_/, "").toLowerCase();
  return V1_LABEL_OVERRIDES[id] || cat?.name || id;
};

/**
 * AddProviderInline - small sub-modal that captures phone/email or name for an unmatched provider.
 * Used inside Plan a Service / Plan around an Occasion when user picks "Add Provider".
 * On save, the parent stores the captured details; the backend will silently match by phone/email
 * via /api/providers/lookup at experience-save time (no outbound SMS/email).
 */
/**
 * AddLinkInline - replaces the legacy "Link a product" modal. Now lets the user
 * link any of: a master product, one of their saved preference cards, or
 * an inspo/upload image. Image URL text inputs are removed in favor of
 * ImagePicker-backed uploads (Iter 9.28). "Buy link" was retired.
 */
const AddProductInline = ({ open, onClose, onSelect, category }) => {
  const [tab, setTab] = useState("search");
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [brandFilter, setBrandFilter] = useState("");
  // Saved preference cards (for "link a card" target)
  const [cards, setCards] = useState([]);
  const [cardSearch, setCardSearch] = useState("");
  const [loadingCards, setLoadingCards] = useState(false);
  // Add new
  const [newName, setNewName] = useState("");
  const [newImages, setNewImages] = useState([]); // ImagePicker now drives this
  const [newLink, setNewLink] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const token = localStorage.getItem("session_token");
    fetch(`${API}/master-products?category=${category || ""}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : { products: [] })
      .then(d => {
        // Strip dummy seed entries (e.g. "Silk press" etc.) from category retail/style
        const list = (d.products || []).filter(p => {
          if (!p?.name) return false;
          const n = p.name.trim().toLowerCase();
          // Hard block legacy seed dummy values
          if (n === "silk press" || n === "test product" || n === "sample") return false;
          return true;
        });
        setProducts(list);
      })
      .finally(() => setLoading(false));
  }, [open, category]);

  // Load user's saved preference cards (for searchable dropdown)
  useEffect(() => {
    if (!open || tab !== "card") return;
    setLoadingCards(true);
    const token = localStorage.getItem("session_token");
    fetch(`${API}/preference-cards`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { cards: [] })
      .then(d => setCards(d.cards || d.items || []))
      .finally(() => setLoadingCards(false));
  }, [open, tab]);

  const brands = Array.from(new Set(products.map(p => p.brand).filter(Boolean))).sort();
  const filtered = products.filter(p =>
    (!search || (p.name || "").toLowerCase().includes(search.toLowerCase()) || (p.brand || "").toLowerCase().includes(search.toLowerCase())) &&
    (!brandFilter || p.brand === brandFilter)
  );
  const filteredCards = cards.filter(c =>
    !cardSearch || (c.name || "").toLowerCase().includes(cardSearch.toLowerCase())
                 || (c.category || "").toLowerCase().includes(cardSearch.toLowerCase())
  );

  const handleAddNew = async () => {
    if (!newName.trim()) {
      toast.error("Add a name for the link");
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem("session_token");
      const primaryImg = newImages?.[0] || null;
      const r = await fetch(`${API}/host/products`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: newName.trim(),
          category: category || null,
          image_url: primaryImg,
          link: newLink.trim() || null,
          description: newDescription.trim() || null,
          status: "pending"
        })
      });
      if (r.ok) {
        const data = await r.json();
        const p = data.product || data;
        toast.success("Link saved (pending admin review)");
        onSelect?.({
          product_id: p.product_id || p.submission_id || `tmp_${Date.now()}`,
          name: newName,
          image_url: primaryImg,
          link_type: "custom",
          _pending: true,
        });
        setNewName(""); setNewImages([]); setNewLink(""); setNewDescription("");
        onClose?.();
      } else {
        toast.error("Could not save link");
      }
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-md w-[95vw] max-h-[85vh] overflow-y-auto" data-testid="add-product-inline">
        <DialogHeader><DialogTitle>Link</DialogTitle></DialogHeader>
        <div className="flex gap-1 mt-2 border-b" data-testid="link-tabs">
          <button onClick={() => setTab("search")} className={`px-3 py-1.5 text-xs font-medium ${tab === "search" ? "border-b-2 border-gray-900 text-gray-900" : "text-gray-500"}`} data-testid="product-tab-search">Product</button>
          <button onClick={() => setTab("card")} className={`px-3 py-1.5 text-xs font-medium ${tab === "card" ? "border-b-2 border-gray-900 text-gray-900" : "text-gray-500"}`} data-testid="product-tab-card">My cards</button>
          <button onClick={() => setTab("new")} className={`px-3 py-1.5 text-xs font-medium ${tab === "new" ? "border-b-2 border-gray-900 text-gray-900" : "text-gray-500"}`} data-testid="product-tab-new">+ Add new</button>
        </div>
        {tab === "search" ? (
          <div className="space-y-2 mt-2">
            <Input
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="product-search-input"
            />
            {/* Brand filter chips */}
            {brands.length > 0 && (
              <div className="flex flex-wrap gap-1" data-testid="brand-filter">
                <button
                  onClick={() => setBrandFilter("")}
                  className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${brandFilter === "" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"}`}
                  data-testid="brand-filter-all"
                >All brands</button>
                {brands.map(b => (
                  <button
                    key={b}
                    onClick={() => setBrandFilter(b)}
                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium truncate max-w-[120px] ${brandFilter === b ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"}`}
                    data-testid={`brand-filter-${b.toLowerCase().replace(/\s+/g,"-")}`}
                    title={b}
                  >{b}</button>
                ))}
              </div>
            )}
            <div className="max-h-72 overflow-y-auto space-y-1">
              {loading ? (
                <div className="py-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
              ) : filtered.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">No products found. Add a new one.</p>
              ) : filtered.map(p => (
                <button
                  key={p.product_id}
                  onClick={() => { onSelect?.({ ...p, link_type: "product" }); onClose?.(); }}
                  className="w-full flex items-center gap-3 px-2 py-2 rounded hover:bg-gray-100 text-left"
                  data-testid={`product-row-${p.product_id}`}
                >
                  {p.image_url ? (
                    <img src={p.image_url} className="w-10 h-10 rounded object-cover bg-gray-100 flex-shrink-0" alt="" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <Package className="w-4 h-4 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{p.name}</p>
                    <p className="text-[10px] text-gray-400 truncate">{p.brand || ""}{p.brand && p.description ? " · " : ""}{p.description || ""}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : tab === "card" ? (
          <div className="space-y-2 mt-2">
            <Input
              placeholder="Search your saved cards..."
              value={cardSearch}
              onChange={(e) => setCardSearch(e.target.value)}
              data-testid="card-search-input"
            />
            <div className="max-h-72 overflow-y-auto space-y-1">
              {loadingCards ? (
                <div className="py-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
              ) : filteredCards.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">No saved cards match. Save one from your /me page first.</p>
              ) : filteredCards.map(c => (
                <button
                  key={c.card_id}
                  onClick={() => {
                    onSelect?.({
                      product_id: c.card_id,
                      name: c.name || "Saved card",
                      image_url: c.cover_image_url || c.image_url || null,
                      link_type: "preference_card",
                      card_id: c.card_id,
                    });
                    onClose?.();
                  }}
                  className="w-full flex items-center gap-3 px-2 py-2 rounded hover:bg-gray-100 text-left"
                  data-testid={`card-row-${c.card_id}`}
                >
                  {c.cover_image_url || c.image_url ? (
                    <img src={c.cover_image_url || c.image_url} className="w-10 h-10 rounded object-cover bg-gray-100 flex-shrink-0" alt="" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{c.name || "Untitled card"}</p>
                    <p className="text-[10px] text-gray-400 truncate capitalize">{c.category || "-"}{c.status ? ` · ${c.status}` : ""}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">Name *</label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Name (product, inspo, look...)" data-testid="new-product-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">Image (upload)</label>
              <ImagePicker
                selectedImages={newImages}
                onSelect={(urls) => setNewImages(urls.slice(0, 1))}
                category={category}
                maxImages={1}
                compact
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">Link (optional)</label>
              <Input value={newLink} onChange={(e) => setNewLink(e.target.value)} placeholder="https://..." data-testid="new-product-link" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">Description (optional)</label>
              <Input value={newDescription} onChange={(e) => setNewDescription(e.target.value)} placeholder="Short description" data-testid="new-product-desc" />
            </div>
            <p className="text-[11px] text-gray-400 leading-tight">
              New entries go to admin for review. They become available platform-wide once approved.
            </p>
            <Button
              onClick={handleAddNew}
              disabled={submitting}
              className="w-full h-10 bg-gray-900 hover:bg-gray-800 text-white"
              data-testid="new-product-save"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Save link
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * AddProviderInline - small sub-modal that captures phone/email or name for an unmatched provider.
 * Used inside Plan a Service / Plan around an Occasion when user picks "Add Provider".
 * On save, the parent stores the captured details; the backend will silently match by phone/email
 * via /api/providers/lookup at experience-save time (no outbound SMS/email).
 */
const AddProviderInline = ({ open, onClose, onSave, serviceLabel = "this service", category = "" }) => {
  const [tab, setTab] = useState("existing");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [addressDetails, setAddressDetails] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // My Providers list (multi-select)
  const [allMyProviders, setAllMyProviders] = useState([]);
  const [selectedProviderIds, setSelectedProviderIds] = useState(new Set());
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [providerSearch, setProviderSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoadingProviders(true);
    const token = localStorage.getItem("session_token");
    if (!token) { setLoadingProviders(false); return; }
    // Iter 9.50+ R3: Pull from BOTH endpoints so the inline picker shows:
    //  - /user/providers/relations -> Phygital users I follow as a provider
    //  - /user/providers           -> Saved provider contacts (mini-CRM)
    // Merge by phygital_user_id (relations) or provider_id (saved contacts).
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${API}/user/providers/relations`, { headers }).then(r => r.ok ? r.json() : { relations: [] }).catch(() => ({ relations: [] })),
      fetch(`${API}/user/providers`, { headers }).then(r => r.ok ? r.json() : { providers: [] }).catch(() => ({ providers: [] })),
    ]).then(([rel, saved]) => {
      const out = [];
      const seen = new Set();
      // Relations first (Phygital user matches keep richer profile data)
      for (const r of (rel.relations || [])) {
        const key = r.provider_user_id;
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(r);
      }
      // Then saved contacts (mini-CRM rows). If a contact links to a phygital_user_id
      // already in `seen`, skip the duplicate. Otherwise expose using provider_id as the key.
      for (const p of (saved.providers || [])) {
        const phyId = p.phygital_user_id;
        if (phyId && seen.has(phyId)) continue;
        const key = phyId || p.provider_id;
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push({
          provider_user_id: key,
          name: p.name,
          profile_image: p.phygital_profile?.profile_image || null,
          category: p.category || null,
          business_name: p.name,
          city: null,
          specialties: [],
          since: p.created_at,
          relation: "saved_contact",
          email: p.email || null,
          phone: p.phone || null,
          is_saved_contact: !phyId,
        });
      }
      setAllMyProviders(out);
    }).finally(() => setLoadingProviders(false));
  }, [open]);

  const reset = () => {
    setName(""); setPhone(""); setEmail(""); setAddress(""); setAddressDetails(null); setSubmitting(false);
    setSelectedProviderIds(new Set()); setProviderSearch(""); setTab("existing");
  };

  const toggleProvider = (id) => {
    const next = new Set(selectedProviderIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedProviderIds(next);
  };

  const saveExisting = () => {
    if (selectedProviderIds.size === 0) {
      toast.error("Pick at least one provider");
      return;
    }
    const picked = allMyProviders.filter(p => selectedProviderIds.has(p.provider_user_id));
    onSave?.({
      multi: true,
      providers: picked.map(p => ({
        name: p.name,
        matched_user_id: p.provider_user_id,
        phone: null,
        email: null,
        category: p.category,
      })),
      // For backward compatibility, set primary fields from the first pick
      name: picked[0]?.name,
      matched_user_id: picked[0]?.provider_user_id,
    });
    reset();
    onClose?.();
  };

  const saveNew = async () => {
    if (!name && !phone && !email) {
      toast.error("Add a name, phone, or email");
      return;
    }
    setSubmitting(true);
    let matched_user_id = null;
    if (phone || email) {
      try {
        const token = localStorage.getItem("session_token");
        const r = await fetch(`${API}/providers/lookup`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ phone: phone || null, email: email || null })
        });
        if (r.ok) {
          const data = await r.json();
          matched_user_id = data?.user_id || null;
        }
      } catch (_) { /* silent */ }
    }
    onSave?.({
      name: name.trim() || null,
      phone: phone.trim() || null,
      email: email.trim() || null,
      address: address.trim() || null,
      address_details: addressDetails || null,
      matched_user_id,
    });
    reset();
    onClose?.();
  };

  const filteredProviders = allMyProviders.filter(p =>
    !providerSearch || (p.name || "").toLowerCase().includes(providerSearch.toLowerCase())
  );
  // Show category-matched first
  const sortedProviders = [...filteredProviders].sort((a, b) => {
    const aMatch = category && a.category && (String(a.category).replace(/^cat_/, "") === category) ? 0 : 1;
    const bMatch = category && b.category && (String(b.category).replace(/^cat_/, "") === category) ? 0 : 1;
    return aMatch - bMatch;
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose?.(); } }}>
      <DialogContent className="max-w-md w-[95vw] max-h-[85vh] overflow-y-auto" data-testid="add-provider-inline">
        <DialogHeader>
          <DialogTitle>Add a provider for {serviceLabel}</DialogTitle>
        </DialogHeader>
        <div className="flex gap-1 mt-2 border-b">
          <button onClick={() => setTab("existing")} className={`px-3 py-1.5 text-xs font-medium ${tab === "existing" ? "border-b-2 border-gray-900 text-gray-900" : "text-gray-500"}`} data-testid="provider-tab-existing">My providers ({allMyProviders.length})</button>
          <button onClick={() => setTab("new")} className={`px-3 py-1.5 text-xs font-medium ${tab === "new" ? "border-b-2 border-gray-900 text-gray-900" : "text-gray-500"}`} data-testid="provider-tab-new">+ Add new</button>
        </div>

        {tab === "existing" ? (
          <div className="space-y-2 mt-2">
            <Input
              placeholder="Search your providers..."
              value={providerSearch}
              onChange={(e) => setProviderSearch(e.target.value)}
              data-testid="provider-search"
            />
            {loadingProviders ? (
              <div className="py-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
            ) : sortedProviders.length === 0 ? (
              <div className="py-4 text-center">
                <p className="text-xs text-gray-400 mb-2">No providers linked yet</p>
                <button onClick={() => setTab("new")} className="text-xs text-gray-700 underline">+ Add your first provider</button>
              </div>
            ) : (
              <div className="max-h-60 overflow-y-auto space-y-1" data-testid="my-providers-multi-list">
                {sortedProviders.map(p => {
                  const checked = selectedProviderIds.has(p.provider_user_id);
                  const inCategory = category && p.category && String(p.category).replace(/^cat_/, "") === category;
                  return (
                    <button
                      key={p.provider_user_id}
                      onClick={() => toggleProvider(p.provider_user_id)}
                      className={`w-full flex items-center gap-2 px-2 py-2 rounded text-left ${checked ? "bg-gray-100 ring-1 ring-gray-900" : "hover:bg-gray-50"}`}
                      data-testid={`provider-multi-${p.provider_user_id}`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${checked ? "bg-gray-900 border-gray-900" : "border-gray-300"}`}>
                        {checked && <Check className="w-3 h-3 text-white" />}
                      </div>
                      {p.profile_image ? (
                        <img src={p.profile_image} className="w-8 h-8 rounded-full object-cover flex-shrink-0" alt="" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                          <User className="w-3.5 h-3.5 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{p.name}</p>
                        <p className="text-[10px] text-gray-500 truncate">
                          {p.business_name || p.category || ""}
                          {inCategory && <span className="ml-1 px-1 rounded bg-green-100 text-green-700">match</span>}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            <Button
              onClick={saveExisting}
              disabled={selectedProviderIds.size === 0}
              className="w-full h-10 bg-gray-900 hover:bg-gray-800 text-white"
              data-testid="add-provider-save-existing"
            >
              <Check className="w-4 h-4 mr-2" /> Link {selectedProviderIds.size || ""} provider{selectedProviderIds.size === 1 ? "" : "s"}
            </Button>
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">
                <User className="w-3 h-3 inline mr-1" /> Provider name
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Naomi"
                data-testid="add-provider-name"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">
                <Phone className="w-3 h-3 inline mr-1" /> Phone (optional)
              </label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                data-testid="add-provider-phone"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">
                <Mail className="w-3 h-3 inline mr-1" /> Email (optional)
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="provider@email.com"
                data-testid="add-provider-email"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">
                <MapPin className="w-3 h-3 inline mr-1" /> Address (optional)
              </label>
              <GooglePlacesAutocomplete
                value={address}
                onChange={(val, details) => { setAddress(val); setAddressDetails(details || null); }}
                placeholder="Studio or salon address"
                data-testid="add-provider-address"
              />
            </div>
            <p className="text-[11px] text-gray-400 leading-tight">
              We will silently match by phone or email if they are already on Phygital. Nothing gets sent.
            </p>
            <Button
              onClick={saveNew}
              disabled={submitting}
              className="w-full h-10 bg-gray-900 hover:bg-gray-800 text-white"
              data-testid="add-provider-save"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save provider
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * PlanExperienceModal - Streamlined single service experience flow
 * Category -> Quick config (date TBD, provider, inspo from folder, cover image) -> Save
 */

// Iter 9.50+ R5 - 15-minute time slots from 8am-9pm
const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 8; h <= 21; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      slots.push(`${hh}:${mm}`);
    }
  }
  return slots;
})();

function formatTimeLabel(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
  const ampm = h >= 12 ? "PM" : "AM";
  const hh12 = ((h + 11) % 12) + 1;
  return `${hh12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function TimeSlotPicker({ value, onChange, testid, disabled }) {
  return (
    <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 mb-2 max-h-48 overflow-y-auto p-1 border border-gray-100 rounded-lg" data-testid={testid}>
      {TIME_SLOTS.map((slot) => (
        <button
          key={slot}
          type="button"
          disabled={disabled}
          onClick={() => onChange(slot)}
          className={`px-2 py-1.5 rounded text-[11px] font-medium transition-all ${
            value === slot ? "bg-gray-900 text-white" : "bg-gray-50 hover:bg-gray-200 text-gray-700"
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          data-testid={`${testid}-${slot}`}
        >
          {formatTimeLabel(slot)}
        </button>
      ))}
    </div>
  );
}

// Iter 9.50+ R7 - Plan state machine. Each status has its own pill style and label.
const STATUS_META = {
  planning:            { label: "Planning",            tone: "bg-stone-100 text-stone-700 border-stone-200" },
  preferences_shared:  { label: "Preferences shared",  tone: "bg-blue-50 text-blue-700 border-blue-200" },
  slot_proposed:       { label: "Slot proposed",       tone: "bg-amber-50 text-amber-700 border-amber-200" },
  provider_notified:   { label: "Provider notified",   tone: "bg-amber-50 text-amber-700 border-amber-200" },
  provider_confirmed:  { label: "Provider confirmed",  tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  diy_only:            { label: "DIY",                 tone: "bg-purple-50 text-purple-700 border-purple-200" },
  self_completed:      { label: "Self-completed",      tone: "bg-purple-50 text-purple-700 border-purple-200" },
  completed:           { label: "Completed",           tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled:           { label: "Cancelled",           tone: "bg-red-50 text-red-700 border-red-200" },
  rescheduled:         { label: "Rescheduled",         tone: "bg-amber-50 text-amber-700 border-amber-200" },
  archived:            { label: "Archived",            tone: "bg-stone-50 text-stone-500 border-stone-200" },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.planning;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium uppercase tracking-wider ${meta.tone}`} data-testid={`plan-status-badge-${status}`}>
      {meta.label}
    </span>
  );
}

// Iter 9.50+ R10 - Preview Preference Card modal. Renders a card-style preview
// of everything currently selected in the Plan modal (service + time + prefs
// with images + products + inspo + budget). Read-only - the user can review
// before saving. Closes back to the Plan modal.
function PreferenceCardPreviewModal({
  open, onClose, experienceName, selectedCategory, selectedService,
  experienceDate, experienceTime, slotMode, secondarySlots, planStatus,
  preferenceAnswers, selectedInspo, budgetService, budgetProducts,
  providerStatus, providerDetails,
}) {
  if (!open) return null;
  const answers = preferenceAnswers || {};
  const productIds = []
    .concat(answers.foundation_shade || [], answers.eyeshadow_palette || [],
            answers.lash_strip_brand || [], answers.lip_products || [],
            answers.setting_spray || [], answers.favorite_products || []);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="preference-card-preview-modal">
        <div className="px-5 pt-4 pb-3 border-b flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Preview - preference card</p>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="preview-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="max-h-[75vh] overflow-y-auto p-5 space-y-4 bg-stone-50">
          {/* Header card */}
          <div className="rounded-2xl bg-white border border-stone-200 overflow-hidden">
            {selectedInspo && selectedInspo.length > 0 && (
              <div className="h-32 w-full bg-stone-100" style={{ backgroundImage: `url(${selectedInspo[0]})`, backgroundSize: "cover", backgroundPosition: "center" }} data-testid="preview-cover-image" />
            )}
            <div className="p-4 space-y-2">
              <div className="flex items-center gap-2">
                <StatusBadge status={planStatus} />
                {selectedCategory && (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-stone-300 text-stone-700">{selectedCategory.name}</span>
                )}
              </div>
              <h2 className="text-lg font-bold text-stone-900" data-testid="preview-title">
                {experienceName || (selectedService?.name ? `${selectedCategory?.name || "Service"} - ${selectedService.name}` : selectedCategory?.name || "Experience")}
              </h2>
              <div className="flex items-center gap-3 text-xs text-stone-600">
                <span className="flex items-center gap-1"><CalendarIcon className="w-3 h-3" />
                  {experienceDate ? experienceDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Date TBD"}
                </span>
                {experienceTime && (
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatTimeLabel(experienceTime)}</span>
                )}
              </div>
              {slotMode !== "single" && secondarySlots?.length > 0 && (
                <p className="text-[10px] text-stone-500 italic" data-testid="preview-alternates">
                  {secondarySlots.filter(s => s.time || s.date).length} alternate slot(s) offered
                </p>
              )}
              {providerStatus === "diy" && (
                <p className="text-[10px] text-purple-700">DIY - no provider assigned</p>
              )}
              {providerStatus === "pending" && providerDetails?.name && (
                <p className="text-[10px] text-stone-700">Provider: <span className="font-medium">{providerDetails.name}</span></p>
              )}
            </div>
          </div>

          {/* Preferences grid */}
          {Object.keys(answers).length > 0 && (
            <div className="rounded-2xl bg-white border border-stone-200 p-4 space-y-3" data-testid="preview-preferences">
              <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Preferences</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {Object.entries(answers).map(([k, v]) => {
                  if (!v || (Array.isArray(v) && v.length === 0)) return null;
                  const label = k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                  const val = Array.isArray(v) ? v.join(", ") : String(v);
                  // Skip product-id arrays - render them separately below.
                  if (Array.isArray(v) && v.some(item => String(item).startsWith("mp_") || String(item).startsWith("ugc_"))) return null;
                  return (
                    <div key={k} className="space-y-0.5">
                      <p className="text-[9px] uppercase tracking-wider text-stone-400">{label}</p>
                      <p className="text-stone-800 font-medium capitalize" data-testid={`preview-pref-${k}`}>{val.replace(/_/g, " ")}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Products with images */}
          {productIds.length > 0 && (
            <ProductPreviewGrid productIds={productIds} />
          )}

          {/* Inspo gallery */}
          {selectedInspo && selectedInspo.length > 0 && (
            <div className="rounded-2xl bg-white border border-stone-200 p-4 space-y-2" data-testid="preview-inspo-gallery">
              <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Inspiration</p>
              <div className="grid grid-cols-3 gap-1.5">
                {selectedInspo.slice(0, 9).map((url, i) => (
                  <div key={i} className="aspect-square rounded-lg bg-stone-100 overflow-hidden border border-stone-200" data-testid={`preview-inspo-${i}`}>
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Budget */}
          {(budgetService || budgetProducts) && (
            <div className="rounded-2xl bg-white border border-stone-200 p-4 space-y-1.5" data-testid="preview-budget">
              <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Budget</p>
              <div className="grid grid-cols-2 gap-3 text-xs">
                {budgetService && (
                  <div>
                    <p className="text-[9px] uppercase tracking-wider text-stone-400">Service</p>
                    <p className="text-stone-900 font-semibold text-base">${budgetService}</p>
                  </div>
                )}
                {budgetProducts && (
                  <div>
                    <p className="text-[9px] uppercase tracking-wider text-stone-400">Products</p>
                    <p className="text-stone-900 font-semibold text-base">${budgetProducts}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t bg-white flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 h-10 text-xs" data-testid="preview-back-btn">Back to edit</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Inline product preview grid - fetches master_products by id and renders
// thumbnails with brand+name. Used inside the preference card preview.
function ProductPreviewGrid({ productIds }) {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    const ids = [...new Set(productIds)].filter(Boolean);
    if (ids.length === 0) return;
    const token = localStorage.getItem("session_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API}/master-products?product_ids=${ids.join(",")}&limit=50`, { headers })
      .then(r => r.ok ? r.json() : { products: [] })
      .then(d => setProducts(d.products || []))
      .catch(() => setProducts([]));
  }, [productIds]);
  if (products.length === 0) return null;
  return (
    <div className="rounded-2xl bg-white border border-stone-200 p-4 space-y-2" data-testid="preview-products-grid">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Products in this card</p>
      <div className="grid grid-cols-3 gap-2">
        {products.map(p => {
          const id = p.product_id || p.master_product_id || p.id;
          const img = p.image_url || p.thumbnail_url;
          return (
            <div key={id} className="rounded-lg border border-stone-200 overflow-hidden bg-stone-50" data-testid={`preview-product-${id}`}>
              <div className="aspect-square bg-stone-100">
                {img ? <img src={img} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-stone-300"><Package className="w-5 h-5" /></div>}
              </div>
              <div className="p-1.5">
                <p className="text-[9px] uppercase tracking-wider text-stone-500 truncate">{p.brand_name || p.brand}</p>
                <p className="text-[10px] text-stone-800 font-medium leading-tight line-clamp-2">{p.name}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export const PlanExperienceModal = ({
  isOpen,
  onClose,
  onSelect,
  liveCategoryIds = null,      // Iter 9.40 - admin-controlled Launch Control gating; null = use V1 default
  waitlistCategoryIds = [],
  // Iter 9.50+ R9 - lets the parent (e.g. PlanEventModal) skip step 1 and
  // jump straight into editing a single service inside an occasion.
  prefilledCategoryId = null,
  prefilledExperienceDate = null,
  prefilledOccasionId = null,
  existingPlan = null,
}) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  // Iter 9.50+ R8 - Per-category service selector. Pulled from
  // /api/services/templates/{category} when a category is picked, plus a
  // "+ Add new service" path that POSTs to /api/user-services and instantly
  // selects the new entry.
  const [serviceOptions, setServiceOptions] = useState([]);
  const [selectedService, setSelectedService] = useState(null); // { id, name, duration_minutes, default_price, source }
  const [showAddNewService, setShowAddNewService] = useState(false);
  const [newServiceForm, setNewServiceForm] = useState({ name: "", duration_minutes: "", default_price: "" });
  const [savingNewService, setSavingNewService] = useState(false);
  const [step, setStep] = useState(1);
  const [experienceName, setExperienceName] = useState("");
  const [experienceDate, setExperienceDate] = useState(null);
  const [experienceTime, setExperienceTime] = useState(""); // "HH:MM"
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  // Iter 9.50+ R5 - Multi-slot scheduling. slotMode controls how secondary
  // slots are interpreted; secondarySlots are alternates the user is open to.
  //   "single"             -> just one primary date+time
  //   "flexible_time"      -> primary date is locked, multiple times offered
  //   "flexible_datetime"  -> multiple date+time pairs offered
  const [slotMode, setSlotMode] = useState("single");
  const [secondarySlots, setSecondarySlots] = useState([]); // [{date: Date|string, time: "HH:MM"}]
  // Iter 9.50+ R5 - Lock state. When a provider confirms a specific slot
  // (via Acuity sync / host accept), primarySlotLocked becomes the matched
  // slot id and secondaries are visually dimmed.
  const [primarySlotLocked, setPrimarySlotLocked] = useState(false);
  // Iter 9.50+ R6 - Per-service budgets. budgetService = what they expect to
  // pay the provider; budgetProducts = what they expect to spend on products
  // (only relevant when provider is DIY or host requires buy-ahead).
  const [budgetService, setBudgetService] = useState("");
  const [budgetProducts, setBudgetProducts] = useState("");
  // Iter 9.50+ R13 - Location section. Three modes:
  //   "tbd"      - city autocomplete only (city, state, country)
  //   "provider" - read-only, inherited from selected provider's directory address
  //   "travel"   - full address + parking/access notes the host needs to travel to
  const [locationMode, setLocationMode] = useState("tbd");
  const [locationCity, setLocationCity] = useState("");
  const [locationAddress, setLocationAddress] = useState("");
  const [locationNotes, setLocationNotes] = useState("");
  const [citySuggestions, setCitySuggestions] = useState([]);
  // Iter 9.50+ R7 - Planning state machine. Possible values:
  //   "planning" | "preferences_shared" | "slot_proposed" | "provider_notified"
  //   "provider_confirmed" | "diy_only" | "self_completed" | "completed"
  //   "cancelled" | "rescheduled" | "archived"
  const [planStatus, setPlanStatus] = useState("planning");
  const [showPreviewCard, setShowPreviewCard] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [providerStatus, setProviderStatus] = useState('tbd');
  
  // Inspo state
  const [userInspo, setUserInspo] = useState([]);
  const [selectedInspo, setSelectedInspo] = useState([]);
  const [coverImageUrl, setCoverImageUrl] = useState(null);
  const [loadingInspo, setLoadingInspo] = useState(false);
  
  // Inline add-provider state
  const [showAddProviderInline, setShowAddProviderInline] = useState(false);
  const [providerDetails, setProviderDetails] = useState(null);
  const [linkedProviderIds, setLinkedProviderIds] = useState([]);  // multi-select provider user ids
  
  // Linked products
  const [masterProducts, setMasterProducts] = useState([]);
  const [linkedProductIds, setLinkedProductIds] = useState([]);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  
  // My Providers (R12) - silent listing, no public discovery
  const [myProviders, setMyProviders] = useState([]);
  const [myProvidersOpen, setMyProvidersOpen] = useState(false);
  
  // Linked occasion (existing planned event/occasion)
  const [userOccasions, setUserOccasions] = useState([]);
  const [linkedOccasionId, setLinkedOccasionId] = useState(null);

  // Iter 9.28 - when ?occasion=<id> is present in the URL, prefill the linked occasion
  // so the planning flow becomes "Plan a service into THIS occasion".
  useEffect(() => {
    if (!isOpen) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const occ = params.get("occasion");
      if (occ) setLinkedOccasionId(occ);
    } catch (_) { /* SSR / older browsers - silent */ }
  }, [isOpen]);

  // Iter 9.50+ R8 - When a category is selected, fetch starter service templates
  // + the user's own UGC services for that category. Reset the picked service.
  useEffect(() => {
    if (!selectedCategory?.id) { setServiceOptions([]); setSelectedService(null); return; }
    const token = localStorage.getItem("session_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const catKey = String(selectedCategory.id).replace(/^cat_/, "").toLowerCase();
    Promise.all([
      fetch(`${API}/services/templates/${catKey}`, { headers }).then(r => r.ok ? r.json() : { templates: [] }).catch(() => ({ templates: [] })),
      fetch(`${API}/user-services?category=${catKey}`, { headers }).then(r => r.ok ? r.json() : { services: [] }).catch(() => ({ services: [] })),
    ]).then(([tpl, ugc]) => {
      const list = [];
      for (const t of (tpl.templates || [])) {
        list.push({ id: t.template_id || `tpl_${(t.name || "").toLowerCase().replace(/\s+/g, "_")}`, name: t.name, duration_minutes: t.duration_minutes || null, default_price: t.default_price || null, source: "template" });
      }
      for (const s of (ugc.services || [])) {
        list.push({ id: s.service_id, name: s.name, duration_minutes: s.duration_minutes || null, default_price: s.default_price || null, source: "ugc" });
      }
      setServiceOptions(list);
    });
    setSelectedService(null);
  }, [selectedCategory]);

  const submitNewService = async () => {
    const name = (newServiceForm.name || "").trim();
    if (!name || !selectedCategory?.id) return;
    setSavingNewService(true);
    try {
      const token = localStorage.getItem("session_token");
      const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const catKey = String(selectedCategory.id).replace(/^cat_/, "").toLowerCase();
      const body = {
        name,
        category: catKey,
        duration_minutes: newServiceForm.duration_minutes ? Number(newServiceForm.duration_minutes) : null,
        default_price: newServiceForm.default_price ? Number(newServiceForm.default_price) : null,
      };
      const r = await fetch(`${API}/user-services`, { method: "POST", headers, body: JSON.stringify(body) });
      if (!r.ok) { setSavingNewService(false); return; }
      const created = await r.json();
      const svc = created.service || created;
      const next = { id: svc.service_id, name: svc.name, duration_minutes: svc.duration_minutes || null, default_price: svc.default_price || null, source: "ugc" };
      setServiceOptions((prev) => [...prev, next]);
      setSelectedService(next);
      setShowAddNewService(false);
      setNewServiceForm({ name: "", duration_minutes: "", default_price: "" });
    } finally {
      setSavingNewService(false);
    }
  };
  
  // Preferences multi-select (synced from admin canonical schema + user defaults)
  const [preferenceChips, setPreferenceChips] = useState([]); // [{key,label,group}]
  // Slice I.a - full human-friendly preference grid: { [field.key]: ['option_id', …] }
  const [preferenceAnswers, setPreferenceAnswers] = useState({});
  const [selectedPreferences, setSelectedPreferences] = useState([]);
  const [userDefaultsLoaded, setUserDefaultsLoaded] = useState(false);

  // Style/Retail-only size storage (Iter 9.28). Pulled from user defaults; saved on commit.
  const [styleSizes, setStyleSizes] = useState({
    top: "",
    bottom: "",
    shoe: "",
    dress: "",
    bra: "",
    notes: "",
  });

  // Iter 9.39a - Style/Retail brand selection (master_brands directory).
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [styleLinkCategory, setStyleLinkCategory] = useState(""); // top|bottom|dress|shoes|accessories|outerwear|lingerie
  
  const { serviceCategories, loading: categoriesLoading } = useExperienceCategories();

  // Iter 9.50+ R9 - jump-into-step-2 when invoked from PlanEventModal Edit pencil.
  useEffect(() => {
    if (!isOpen) return;
    if (prefilledCategoryId && !selectedCategory) {
      // Iter 9.53 - Makeup uses the NEW /plan/makeup build; deep-link routes there.
      const pid = String(prefilledCategoryId).toLowerCase();
      if (pid === "makeup" || pid === "cat_makeup") {
        onClose?.();
        window.location.assign("/plan/makeup");
        return;
      }
      const cat = (serviceCategories || []).find((c) => c.id === prefilledCategoryId);
      if (cat) {
        setSelectedCategory(cat);
        setStep(2);
        if (prefilledExperienceDate) setExperienceDate(prefilledExperienceDate);
        if (prefilledOccasionId) setLinkedOccasionId(prefilledOccasionId);
        if (existingPlan) {
          if (existingPlan.service_id || existingPlan.service_name) {
            setSelectedService({
              id: existingPlan.service_id,
              name: existingPlan.service_name,
              duration_minutes: existingPlan.service_duration_minutes,
              default_price: existingPlan.service_default_price,
              source: existingPlan.service_source || "template",
            });
          }
          if (existingPlan.time) setExperienceTime(existingPlan.time);
          if (existingPlan.slot_mode) setSlotMode(existingPlan.slot_mode);
          if (Array.isArray(existingPlan.secondary_slots)) setSecondarySlots(existingPlan.secondary_slots);
          if (existingPlan.budget_service != null) setBudgetService(String(existingPlan.budget_service));
          if (existingPlan.budget_products != null) setBudgetProducts(String(existingPlan.budget_products));
          if (existingPlan.location_mode) setLocationMode(existingPlan.location_mode);
          if (existingPlan.location_city) setLocationCity(existingPlan.location_city);
          if (existingPlan.location_address) setLocationAddress(existingPlan.location_address);
          if (existingPlan.location_notes) setLocationNotes(existingPlan.location_notes);
          if (existingPlan.plan_status) setPlanStatus(existingPlan.plan_status);
        }
      }
    }
  }, [isOpen, prefilledCategoryId, serviceCategories]);

  // Iter 9.40+ Airtable CMS - popup_plan_{category} surface (e.g. popup_plan_makeup).
  // When a surface doesn't exist yet, slotMap is empty and every <Slot fallback="..."/>
  // renders the existing hardcoded copy, so this is fully backwards-compatible.
  const _catKey = selectedCategory?.id
    ? String(selectedCategory.id).replace(/^cat_/, "").toLowerCase()
    : null;
  const _surfaceId = _catKey ? `popup_plan_${_catKey}` : null;
  const { slotMap: planSlotMap } = useUiSurface(_surfaceId, { enabled: !!_surfaceId });
  // Iter 9.47.k - Shared section labels (Preferences / Brand & link / My sizes / Images)
  // live in `surface_plan_modal` and apply to every category popup.
  const { slotMap: planModal } = useUiSurface("surface_plan_modal");
  // Iter 9.47.m - Per-category `header_*` overrides. If a slot exists in
  // `popup_plan_<category>` (e.g. `popup_plan_hair.header_preferences = "Hair Preferences"`)
  // it takes priority over the shared `surface_plan_modal` default. Naomi
  // can leave it empty to fall back to the shared surface, or set a per-category
  // override without breaking other categories.
  const sectionLabel = (slotKey, sharedFallback) => {
    const overrideKey = slotKey.replace(/^section_/, "header_");
    const perCategory = slotText(planSlotMap, overrideKey, "");
    if (perCategory) return perCategory;
    return slotText(planModal, slotKey, sharedFallback);
  };

  // Fetch inspo when category selected
  useEffect(() => {
    if (!selectedCategory?.id) return;
    const token = localStorage.getItem("session_token");
    if (!token) return;
    setLoadingInspo(true);
    fetch(`${API}/inspo?category=${selectedCategory.id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : { inspo: [] })
      .then(data => {
        setUserInspo(data.inspo || []);
        setLoadingInspo(false);
      })
      .catch(() => setLoadingInspo(false));
  }, [selectedCategory]);
  
  // Fetch master products for the linker when category is picked
  useEffect(() => {
    if (!selectedCategory?.id) return;
    const token = localStorage.getItem("session_token");
    if (!token) return;
    fetch(`${API}/master-products?category=${selectedCategory.id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : { products: [] })
      .then(d => setMasterProducts(d.products || []))
      .catch(() => setMasterProducts([]));
  }, [selectedCategory]);
  
  // Fetch user's My Providers (R12) - filtered by selected category when picked
  // Iter 9.50+ R3: Merge `/user/providers/relations` (Phygital host links) with
  // `/user/providers` (saved provider contacts) so the inline list mirrors the
  // /me Experiences "My Providers" page.
  useEffect(() => {
    if (!selectedCategory?.id) return;
    const token = localStorage.getItem("session_token");
    if (!token) return;
    const cat = String(selectedCategory.id).replace(/^cat_/, "");
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${API}/user/providers/relations?category=${cat}`, { headers }).then(r => r.ok ? r.json() : { relations: [] }).catch(() => ({ relations: [] })),
      fetch(`${API}/user/providers`, { headers }).then(r => r.ok ? r.json() : { providers: [] }).catch(() => ({ providers: [] })),
    ]).then(([rel, saved]) => {
      const out = [];
      const seen = new Set();
      for (const r of (rel.relations || [])) {
        const key = r.provider_user_id;
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(r);
      }
      for (const p of (saved.providers || [])) {
        const phyId = p.phygital_user_id;
        if (phyId && seen.has(phyId)) continue;
        // Category filter for saved contacts: include when category matches OR when no category was saved.
        if (cat && p.category && String(p.category).replace(/^cat_/, "") !== cat) continue;
        const key = phyId || p.provider_id;
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push({
          provider_user_id: key,
          name: p.name,
          profile_image: p.phygital_profile?.profile_image || null,
          category: p.category || null,
          business_name: p.name,
          since: p.created_at,
          relation: "saved_contact",
          is_saved_contact: !phyId,
        });
      }
      setMyProviders(out);
    }).catch(() => setMyProviders([]));
  }, [selectedCategory]);
  
  // Fetch the user's existing occasions/personal events for the link dropdown
  useEffect(() => {
    if (!isOpen) return;
    const token = localStorage.getItem("session_token");
    if (!token) return;
    fetch(`${API}/user/experiences`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { experiences: [] })
      .then(d => {
        const all = d.experiences || d.user_experiences || [];
        // Only include "personal_event" parents
        setUserOccasions(all.filter(e => e.type === "personal_event"));
      })
      .catch(() => {});
  }, [isOpen]);

  // Fetch canonical preference chips for the selected category (admin schema)
  // and pre-fill from user_defaults so the modal mirrors what's saved on the Preferences tab.
  useEffect(() => {
    if (!selectedCategory?.id) return;
    const token = localStorage.getItem("session_token");
    if (!token) return;
    const catKey = String(selectedCategory.id).replace(/^cat_/, "").toLowerCase();
    
    (async () => {
      try {
        // 1. Fetch canonical schema (chips) for this category
        const sRes = await fetch(`${API}/category-preference-schemas?category_id=${encodeURIComponent(catKey)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        let chips = [];
        if (sRes.ok) {
          const sData = await sRes.json();
          // Response shape: { schemas: { catKey: [{block_key, block_label, fields: [{key,label}]}] } }
          const blocks = (sData?.schemas || {})[catKey] || [];
          blocks.forEach(b => {
            (b.fields || []).forEach(f => {
              chips.push({ key: f.key || f.id || f.field_key, label: f.label || f.name || (f.key || "").replace(/_/g, " "), block: b.block_label });
            });
          });
        }
        // Fallback: derive from a small static list per category to avoid empty state
        if (!chips.length) {
          const fallback = {
            hair: ["balayage", "color", "blowout", "extensions", "trim", "treatment"],
            makeup: ["natural", "glam", "bridal", "editorial", "strip_lash", "individual_lash"],
            nails: ["gel", "acrylic", "press_on", "nail_art", "french"],
            lash: ["classic", "hybrid", "volume", "mega_volume", "lash_lift"],
            culinary: ["vegan", "vegetarian", "gluten_free", "kosher", "halal", "nut_free"],
            wellness: ["meditation", "yoga", "breathwork", "sound_bath", "massage"],
            events: ["dietary", "accessibility", "music", "photography"],
          };
          chips = (fallback[catKey] || []).map(k => ({ key: k, label: k.replace(/_/g, " ") }));
        }
        setPreferenceChips(chips);

        // 2. Fetch user preferences + defaults so we can prefill
        if (!userDefaultsLoaded) {
          const [prefsRes, defaultsRes] = await Promise.all([
            fetch(`${API}/user/preferences`, { headers: { Authorization: `Bearer ${token}` } }),
            fetch(`${API}/user/defaults`, { headers: { Authorization: `Bearer ${token}` } }),
          ]);
          let prefilled = [];
          let prefilledAnswers = null;
          if (prefsRes.ok) {
            const pData = await prefsRes.json();
            const catPrefs = pData?.[catKey] || pData?.preferences?.[catKey];
            if (Array.isArray(catPrefs)) prefilled = prefilled.concat(catPrefs);
            else if (catPrefs && typeof catPrefs === "object") {
              // Iter 9.50+ R10 - capture the full sectioned answer map so the
              // preview card + PreferenceSchemaInline can show what's already
              // saved on the user profile.
              prefilledAnswers = catPrefs;
              // Flatten any boolean-true fields back to keys
              Object.entries(catPrefs).forEach(([k, v]) => {
                if (v === true || (Array.isArray(v) && v.length)) prefilled.push(k);
              });
            }
          }
          if (defaultsRes.ok) {
            const dData = await defaultsRes.json();
            const defaults = dData?.defaults || dData || {};
            if (Array.isArray(defaults[catKey])) prefilled = prefilled.concat(defaults[catKey]);
            if (defaults?.events?.interests) prefilled = prefilled.concat((defaults.events.interests || []).filter(i => chips.some(c => c.key === i)));
            // Iter 9.28 prefill style size storage from saved defaults
            const sizeSrc = defaults?.style?.sizes || defaults?.retail?.sizes || defaults?.sizes;
            if (sizeSrc && typeof sizeSrc === "object") {
              setStyleSizes(prev => ({
                ...prev,
                top: sizeSrc.top ?? prev.top,
                bottom: sizeSrc.bottom ?? prev.bottom,
                shoe: sizeSrc.shoe ?? prev.shoe,
                dress: sizeSrc.dress ?? prev.dress,
                bra: sizeSrc.bra ?? prev.bra,
                notes: sizeSrc.notes ?? prev.notes,
              }));
            }
          }
          setSelectedPreferences(Array.from(new Set(prefilled)));
          if (prefilledAnswers && Object.keys(prefilledAnswers).length > 0) {
            setPreferenceAnswers(prev => ({ ...prefilledAnswers, ...prev }));
          }
          setUserDefaultsLoaded(true);
        }
      } catch (e) {
        console.warn("Could not fetch preference schema or defaults", e);
      }
    })();
  }, [selectedCategory, userDefaultsLoaded]);

  const handleCategorySelect = (category) => {
    // Iter 9.53 - Phygital V1: Makeup uses the NEW `/plan/makeup` build.
    // For all other categories (hair/nails/style/lashes/food), fall back to
    // the existing step-2 form for now until we ship their category-specific
    // planners.
    const catId = String(category?.id || "").toLowerCase();
    if (catId === "makeup" || catId === "cat_makeup") {
      onClose?.();
      window.location.assign("/plan/makeup");
      return;
    }
    setSelectedCategory(category);
    setExperienceName(category.name);
    setStep(2);
  };

  const handleCreate = async () => {
    if (!selectedCategory) return;
    
    const inspoItems = selectedInspo.map(id => {
      const found = userInspo.find(i => (i.inspo_id || i.id) === id);
      return found ? { id: found.inspo_id || found.id, image_url: found.image_url } : null;
    }).filter(Boolean);
    
    // Use selected cover or first inspo as cover
    const cover = coverImageUrl || (inspoItems.length > 0 ? inspoItems[0].image_url : null);
    const catKey = String(selectedCategory.id).replace(/^cat_/, "").toLowerCase();
    
    // Slice I.a - Persist the full preferenceAnswers map (sectioned, multi-select)
    // alongside the legacy chip array so plans render exactly the way /me does.
    if (preferenceAnswers && Object.keys(preferenceAnswers).length > 0) {
      try {
        const token = localStorage.getItem("session_token");
        await fetch(`${API}/user/preferences/${encodeURIComponent(catKey)}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ...preferenceAnswers, _source: "plan_modal" }),
        });
      } catch (e) {
        console.warn("Could not save preference answers to defaults", e);
      }
    }

    // Persist preference selections to user_preferences (canonical), which
    // also syncs into user_defaults on the backend for events/accessibility/dietary.
    if (selectedPreferences.length > 0) {
      try {
        const token = localStorage.getItem("session_token");
        // Convert the array of keys into a {key:true} map (the backend stores the
        // category sub-doc as-is, so this lets future reads round-trip correctly).
        const prefMap = selectedPreferences.reduce((acc, k) => { acc[k] = true; return acc; }, {});
        prefMap.selected = selectedPreferences;
        await fetch(`${API}/user/preferences/${encodeURIComponent(catKey)}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(prefMap)
        });
      } catch (e) {
        console.warn("Could not save preferences to defaults", e);
      }
    }

    // Iter 9.28 - Style/Retail size storage. Persist to user defaults.
    if ((catKey === "style" || catKey === "style") && Object.values(styleSizes).some(v => v && String(v).trim())) {
      try {
        const token = localStorage.getItem("session_token");
        const cleaned = Object.fromEntries(
          Object.entries(styleSizes).map(([k, v]) => [k, (v ?? "").toString().trim()])
        );
        await fetch(`${API}/user/preferences/${encodeURIComponent(catKey)}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ sizes: cleaned })
        });
      } catch (e) {
        console.warn("Could not save sizes to defaults", e);
      }
    }
    
    onSelect?.({
      category: selectedCategory.id,
      name: experienceName || `My ${selectedCategory.name} Experience`,
      target_date: experienceDate?.toISOString() || null,
      date: experienceDate?.toISOString() || null,
      provider_status: providerStatus,
      provider_name: providerDetails?.name || null,
      provider_phone: providerDetails?.phone || null,
      provider_email: providerDetails?.email || null,
      provider_user_id: providerDetails?.matched_user_id || null,
      provider_user_ids: linkedProviderIds,
      linked_product_ids: linkedProductIds,
      linked_event_id: linkedOccasionId,
      inspo: inspoItems,
      cover_image: cover,
      preferences: selectedPreferences,
      preference_answers: preferenceAnswers,
      // Iter 9.50+ R8 - picked service (template or UGC)
      service_id: selectedService?.id || null,
      service_name: selectedService?.name || null,
      service_duration_minutes: selectedService?.duration_minutes || null,
      service_default_price: selectedService?.default_price || null,
      // Iter 9.50+ R5 - time + secondary slots
      time: experienceTime || null,
      slot_mode: slotMode,
      secondary_slots: secondarySlots.map((s) => ({
        date: s.date ? (typeof s.date === "string" ? s.date : s.date.toISOString()) : null,
        time: s.time || null,
      })),
      primary_slot_locked: primarySlotLocked,
      // Iter 9.50+ R6 - budgets
      budget_service: budgetService ? Number(budgetService) : null,
      budget_products: budgetProducts ? Number(budgetProducts) : null,
      // Iter 9.50+ R13 - location persisted with the plan so the host can
      // see address/notes once status flips to provider_confirmed.
      location_mode: locationMode,
      location_city: locationMode === "tbd" ? locationCity : null,
      location_address: locationMode === "travel" ? locationAddress : (locationMode === "provider" ? (providerDetails?.address || providerDetails?.directory_address || null) : null),
      location_notes: locationMode === "travel" ? locationNotes : null,
      // Iter 9.50+ R7 - state machine
      plan_status: planStatus,
      sizes: (catKey === "style" || catKey === "style") ? styleSizes : undefined,
      brand_id: (catKey === "style" || catKey === "style") ? (selectedBrandId || undefined) : undefined,
      style_link_category: (catKey === "style" || catKey === "style") ? (styleLinkCategory || undefined) : undefined,
      type: "experience"
    });
    
    resetAndClose();
  };

  const resetAndClose = () => {
    setSelectedCategory(null);
    setSelectedService(null);
    setShowAddNewService(false);
    setNewServiceForm({ name: "", duration_minutes: "", default_price: "" });
    setStep(1);
    setExperienceName("");
    setExperienceDate(null);
    setExperienceTime("");
    setShowDatePicker(false);
    setSlotMode("single");
    setSecondarySlots([]);
    setPrimarySlotLocked(false);
    setBudgetService("");
    setBudgetProducts("");
    setLocationMode("tbd");
    setLocationCity("");
    setLocationAddress("");
    setLocationNotes("");
    setCitySuggestions([]);
    setPlanStatus("planning");
    setEditingName(false);
    setProviderStatus('tbd');
    setUserInspo([]);
    setSelectedInspo([]);
    setCoverImageUrl(null);
    setShowAddProviderInline(false);
    setProviderDetails(null);
    setLinkedProductIds([]);
    setProductPickerOpen(false);
    setLinkedOccasionId(null);
    setSelectedPreferences([]);
    setUserDefaultsLoaded(false);
    setStyleSizes({ top: "", bottom: "", shoe: "", dress: "", bra: "", notes: "" });
    setSelectedBrandId(null);
    setStyleLinkCategory("");
    onClose?.();
  };

  const toggleInspo = (id) => {
    if (selectedInspo.includes(id)) {
      setSelectedInspo(selectedInspo.filter(x => x !== id));
      if (coverImageUrl) {
        const item = userInspo.find(i => (i.inspo_id || i.id) === id);
        if (item?.image_url === coverImageUrl) setCoverImageUrl(null);
      }
    } else {
      setSelectedInspo([...selectedInspo, id]);
      // Auto-set first selected as cover
      if (!coverImageUrl) {
        const item = userInspo.find(i => (i.inspo_id || i.id) === id);
        if (item) setCoverImageUrl(item.image_url);
      }
    }
  };

  const setCover = (url) => {
    setCoverImageUrl(url);
    toast.success("Cover image set!");
  };

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === 1 ? "What type of experience?" : (
              <span className="flex items-center gap-2">
                {(() => { const I = getIconComponent(selectedCategory?.icon, selectedCategory?.id); return <I className="w-5 h-5" />; })()}
                <Slot
                  map={planSlotMap}
                  k="headline"
                  fallback={`Plan Your ${selectedCategory?.name} Experience`}
                />
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Category Selection */}
        {step === 1 && (
          <div className="mt-2" data-testid="category-grid">
            {categoriesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: getCategoryHex("makeup") }} />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {serviceCategories.filter((cat) => {
                  // Iter 9.40 - admin Launch Control takes precedence over V1 fallback
                  if (Array.isArray(liveCategoryIds) && liveCategoryIds.length > 0) {
                    const id = String(cat?.id || "").replace(/^cat_/, "").toLowerCase();
                    const liveSet = new Set(liveCategoryIds.map(s => String(s).replace(/^cat_/, "").toLowerCase()));
                    const waitlistSet = new Set((waitlistCategoryIds || []).map(s => String(s).replace(/^cat_/, "").toLowerCase()));
                    return liveSet.has(id) || waitlistSet.has(id);
                  }
                  return isV1Category(cat);
                }).map((cat) => {
                  const IconComponent = getIconComponent(cat.icon, cat.id);
                  const solidHex = getCategoryHex(cat.id, cat.dark_hex || cat.color);
                  const preset = getCategoryPreset(String(cat.id).replace(/^cat_/, ""));
                  // Light bg from canonical preset (admin-controlled), icon vector in solid hex for contrast
                  const lightBg = preset?.bgColor || `${solidHex}1F`;
                  // Iter 9.40 - waitlist badge if admin gated this cat to waitlist
                  const catId = String(cat?.id || "").replace(/^cat_/, "").toLowerCase();
                  const waitlistSet = new Set((waitlistCategoryIds || []).map(s => String(s).replace(/^cat_/, "").toLowerCase()));
                  const isWaitlist = waitlistSet.has(catId);
                  return (
                    <button
                      key={cat.id}
                      onClick={() => {
                        if (isWaitlist) {
                          toast.info(`${v1Label(cat)} is on waitlist - we'll notify you when it launches.`);
                          return;
                        }
                        handleCategorySelect(cat);
                      }}
                      className={`p-4 rounded-xl border text-left transition-all ${isWaitlist ? "opacity-60" : "hover:shadow-md hover:scale-[1.02]"}`}
                      style={{ backgroundColor: lightBg, borderColor: `${solidHex}33`, color: "#111827" }}
                      data-testid={`category-${cat.id}`}
                    >
                      <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center mb-2 shadow-sm">
                        <IconComponent className="w-5 h-5" style={{ color: solidHex }} />
                      </div>
                      <h3 className="font-semibold text-gray-900 flex items-center gap-1">
                        {v1Label(cat)}
                        {isWaitlist && <Badge className="ml-auto bg-amber-100 text-amber-800 border-0 text-[9px] px-1.5 py-0">Waitlist</Badge>}
                      </h3>
                      <p className="text-xs text-gray-600">{cat.description}</p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Quick Config */}
        {step === 2 && selectedCategory && (
          <div className="space-y-4 mt-2" data-testid="experience-config">
            <button onClick={() => { setStep(1); setSelectedCategory(null); }} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            
            {/* Cover image preview with X-to-remove overlay */}
            {coverImageUrl && (
              <div className="relative h-28 rounded-xl overflow-hidden group">
                <img src={coverImageUrl} alt="Cover" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <Badge className="absolute bottom-2 left-2 bg-white/90 text-gray-700 text-[10px]">
                  <ImageIcon className="w-3 h-3 mr-1" /> Cover Image
                </Badge>
                <button
                  onClick={() => setCoverImageUrl(null)}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 hover:bg-black flex items-center justify-center transition-opacity opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                  aria-label="Remove cover image"
                  data-testid="remove-cover-btn"
                >
                  <X className="w-3.5 h-3.5 text-white" />
                </button>
              </div>
            )}
            
            {/* Category + Name */}
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl" style={{ backgroundColor: hexToSoftBg(getCategoryHex(selectedCategory.id, selectedCategory.dark_hex || selectedCategory.color)) }}>
              {(() => { const I = getIconComponent(selectedCategory.icon, selectedCategory.id); const solid = getCategoryHex(selectedCategory.id, selectedCategory.dark_hex || selectedCategory.color); return <I className="w-5 h-5" style={{ color: solid }} />; })()}
              {editingName ? (
                <Input
                  value={experienceName}
                  onChange={(e) => setExperienceName(e.target.value)}
                  onBlur={() => setEditingName(false)}
                  onKeyDown={(e) => e.key === 'Enter' && setEditingName(false)}
                  autoFocus
                  className="h-7 text-sm border-0 bg-transparent p-0 focus-visible:ring-0"
                />
              ) : (
                <button onClick={() => setEditingName(true)} className="flex items-center gap-1.5 text-sm font-medium">
                  {experienceName || selectedCategory.name}
                  <Edit3 className="w-3 h-3 text-gray-400" />
                </button>
              )}
            </div>

            {/* Service picker - Iter 9.50+ R8 - shows starter templates + the
                user's saved custom services for the picked category, plus a
                "+ Add new service" inline form that saves to /user-services. */}
            <div data-testid="plan-service-picker">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-2">Service</label>
              {!showAddNewService ? (
                <div className="flex flex-wrap gap-1.5">
                  {serviceOptions.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setSelectedService(s)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        selectedService?.id === s.id
                          ? "bg-gray-900 text-white border-gray-900"
                          : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
                      }`}
                      data-testid={`service-option-${s.id}`}
                    >
                      {s.name}
                      {s.source === "ugc" && <span className="ml-1 text-[9px] uppercase opacity-60">yours</span>}
                    </button>
                  ))}
                  <button
                    onClick={() => setShowAddNewService(true)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium border border-dashed border-gray-300 text-gray-600 hover:border-gray-500 hover:text-gray-900 flex items-center gap-1"
                    data-testid="add-new-service-btn"
                  >
                    <Plus className="w-3 h-3" /> Add new service
                  </button>
                </div>
              ) : (
                <div className="space-y-2 p-3 rounded-lg border border-gray-200 bg-stone-50" data-testid="add-new-service-form">
                  <input
                    value={newServiceForm.name}
                    onChange={(e) => setNewServiceForm({ ...newServiceForm, name: e.target.value })}
                    placeholder="Service name (e.g. Sculpted Glam Bridal)"
                    className="w-full px-3 py-1.5 border border-stone-200 rounded-lg text-xs"
                    data-testid="add-new-service-name"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      min="0"
                      value={newServiceForm.duration_minutes}
                      onChange={(e) => setNewServiceForm({ ...newServiceForm, duration_minutes: e.target.value })}
                      placeholder="Duration (min)"
                      className="w-full px-3 py-1.5 border border-stone-200 rounded-lg text-xs"
                      data-testid="add-new-service-duration"
                    />
                    <input
                      type="number"
                      min="0"
                      value={newServiceForm.default_price}
                      onChange={(e) => setNewServiceForm({ ...newServiceForm, default_price: e.target.value })}
                      placeholder="Price ($)"
                      className="w-full px-3 py-1.5 border border-stone-200 rounded-lg text-xs"
                      data-testid="add-new-service-price"
                    />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => { setShowAddNewService(false); setNewServiceForm({ name: "", duration_minutes: "", default_price: "" }); }} className="flex-1 px-3 py-2 rounded-lg border border-stone-200 text-xs font-medium" data-testid="add-new-service-cancel">Cancel</button>
                    <button onClick={submitNewService} disabled={savingNewService || !newServiceForm.name.trim()} className="flex-1 px-3 py-2 rounded-lg bg-stone-900 text-white text-xs font-medium disabled:opacity-60" data-testid="add-new-service-save">{savingNewService ? "Saving..." : "Save service"}</button>
                  </div>
                </div>
              )}
            </div>

            {/* Status badge - Iter 9.50+ R7 */}
            <div className="flex items-center gap-2">
              <StatusBadge status={planStatus} />
              {planStatus !== "planning" && (
                <button onClick={() => setPlanStatus("planning")} className="text-[10px] text-gray-400 underline hover:text-gray-600" data-testid="status-reset-btn">reset</button>
              )}
            </div>

            {/* Date + Time - Iter 9.50+ R5b - cleaner layout
                Single row: TBD vs Pick date toggle. When Pick is chosen we open
                a 2-column grid (calendar on the left, scrollable time slots on
                the right with "Any time" pinned at the top). */}
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-2">When</label>
              <div className="flex gap-2 mb-2">
                <button
                  onClick={() => { setExperienceDate(null); setShowDatePicker(false); setExperienceTime(""); setShowTimePicker(false); }}
                  className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all ${
                    !experienceDate && !showDatePicker ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid="date-tbd-btn"
                >
                  <Clock className="w-3 h-3 inline mr-1" /> Date TBD
                </button>
                <button
                  onClick={() => { setShowDatePicker(true); setShowTimePicker(true); }}
                  className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all ${
                    showDatePicker || experienceDate ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid="date-pick-btn"
                >
                  <CalendarIcon className="w-3 h-3 inline mr-1" />
                  {experienceDate
                    ? `${experienceDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}${experienceTime ? `, ${formatTimeLabel(experienceTime)}` : ', any time'}`
                    : 'Pick date & time'}
                </button>
              </div>
              {showDatePicker && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2 items-start" data-testid="datetime-grid">
                  <Calendar
                    mode="single"
                    selected={experienceDate}
                    onSelect={(date) => { setExperienceDate(date); }}
                    className="rounded-lg border bg-white"
                  />
                  <div className={`rounded-lg border bg-white p-2 ${!experienceDate ? "opacity-50 pointer-events-none" : ""}`} data-testid="time-side-panel">
                    <div className="flex items-center justify-between mb-1.5 px-1">
                      <p className="text-[10px] uppercase tracking-wider text-gray-500">Time</p>
                      {experienceTime && (
                        <button onClick={() => setExperienceTime("")} className="text-[10px] text-gray-400 underline hover:text-gray-700" data-testid="clear-time-btn">clear</button>
                      )}
                    </div>
                    <button
                      onClick={() => setExperienceTime("")}
                      disabled={!experienceDate}
                      className={`w-full mb-1.5 px-2 py-1.5 rounded text-[11px] font-medium transition-all sticky top-0 ${
                        experienceTime === "" ? "bg-gray-900 text-white" : "bg-gray-50 hover:bg-gray-200 text-gray-700"
                      }`}
                      data-testid="time-any-btn"
                    >
                      Any time
                    </button>
                    <div className="grid grid-cols-3 gap-1.5 max-h-72 overflow-y-auto pr-1" data-testid="primary-time-slots">
                      {TIME_SLOTS.map((slot) => (
                        <button
                          key={slot}
                          type="button"
                          disabled={!experienceDate || primarySlotLocked}
                          onClick={() => setExperienceTime(slot)}
                          className={`px-1 py-1.5 rounded text-[10px] font-medium transition-all ${
                            experienceTime === slot ? "bg-gray-900 text-white" : "bg-gray-50 hover:bg-gray-200 text-gray-700"
                          } ${(!experienceDate || primarySlotLocked) ? "opacity-50 cursor-not-allowed" : ""}`}
                          data-testid={`primary-time-slots-${slot}`}
                        >
                          {formatTimeLabel(slot)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Secondary slots - Iter 9.50+ R5 */}
              {experienceDate && (
                <div className="mt-2 space-y-1.5">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-500">
                    <span>Flexibility</span>
                    <div className="flex gap-1">
                      <button onClick={() => setSlotMode("single")} className={`px-2 py-0.5 rounded-full ${slotMode === "single" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"}`} data-testid="slot-mode-single">Locked</button>
                      <button onClick={() => setSlotMode("flexible_time")} className={`px-2 py-0.5 rounded-full ${slotMode === "flexible_time" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"}`} data-testid="slot-mode-flex-time">Flex time</button>
                      <button onClick={() => setSlotMode("flexible_datetime")} className={`px-2 py-0.5 rounded-full ${slotMode === "flexible_datetime" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"}`} data-testid="slot-mode-flex-datetime">Flex date+time</button>
                    </div>
                  </div>
                  {slotMode !== "single" && (
                    <div className="space-y-1.5">
                      {secondarySlots.map((slot, idx) => (
                        <div
                          key={idx}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border border-gray-200 bg-white ${primarySlotLocked ? "opacity-30 line-through" : "opacity-60"}`}
                          data-testid={`secondary-slot-${idx}`}
                        >
                          <span className="text-[10px] text-gray-400 uppercase">alt {idx + 1}</span>
                          {slotMode === "flexible_datetime" && (
                            <input
                              type="date"
                              value={slot.date ? (new Date(slot.date).toISOString().slice(0, 10)) : ""}
                              onChange={(e) => { const next = [...secondarySlots]; next[idx].date = e.target.value ? new Date(e.target.value) : null; setSecondarySlots(next); }}
                              disabled={primarySlotLocked}
                              className="flex-1 px-2 py-1 rounded text-xs border border-gray-200"
                            />
                          )}
                          {slotMode === "flexible_time" && (
                            <span className="text-xs text-gray-500 flex-1">{experienceDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                          )}
                          <input
                            type="time"
                            value={slot.time || ""}
                            onChange={(e) => { const next = [...secondarySlots]; next[idx].time = e.target.value; setSecondarySlots(next); }}
                            disabled={primarySlotLocked}
                            className="px-2 py-1 rounded text-xs border border-gray-200"
                          />
                          <button
                            onClick={() => setSecondarySlots(secondarySlots.filter((_, i) => i !== idx))}
                            disabled={primarySlotLocked}
                            className="text-gray-400 hover:text-red-500 disabled:opacity-30"
                            data-testid={`secondary-slot-remove-${idx}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {secondarySlots.length < 5 && !primarySlotLocked && (
                        <button
                          onClick={() => setSecondarySlots([...secondarySlots, slotMode === "flexible_time" ? { date: experienceDate, time: "" } : { date: null, time: "" }])}
                          className="w-full px-2 py-1.5 rounded-lg border border-dashed border-gray-300 text-gray-500 text-[11px] hover:border-gray-500 hover:text-gray-800 flex items-center justify-center gap-1"
                          data-testid="add-secondary-slot-btn"
                        >
                          <Plus className="w-3 h-3" /> Add alternate {slotMode === "flexible_time" ? "time" : "date+time"} {secondarySlots.length > 0 ? `(${secondarySlots.length}/5)` : ""}
                        </button>
                      )}
                      {primarySlotLocked && (
                        <p className="text-[10px] text-gray-400 italic">Provider confirmed - alternates archived.</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Provider */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Provider</label>
                {/* Iter 9.50+ R5b - My Providers anchored to the Provider header
                    (was sitting below TBD/DIY/Add row, which looked detached). */}
                <button
                  onClick={() => setMyProvidersOpen(!myProvidersOpen)}
                  className="text-[10px] font-medium text-gray-600 hover:text-gray-900 flex items-center gap-1"
                  data-testid="toggle-my-providers"
                >
                  <Users className="w-3 h-3" /> My Providers ({myProviders.length})
                  <ChevronDown className={`w-3 h-3 transition-transform ${myProvidersOpen ? 'rotate-180' : ''}`} />
                </button>
              </div>
              <div className="flex gap-2">
                {[
                  { id: 'tbd', label: 'TBD', icon: Clock },
                  { id: 'diy', label: 'DIY', icon: User },
                  { id: 'pending', label: 'Add Provider', icon: Plus }
                ].map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => {
                      setProviderStatus(opt.id);
                      if (opt.id === 'pending') setShowAddProviderInline(true);
                    }}
                    className={`flex-1 py-2.5 px-2 rounded-lg text-center transition-all ${
                      providerStatus === opt.id 
                        ? 'bg-gray-900 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid={`provider-${opt.id}`}
                  >
                    <opt.icon className="w-4 h-4 mx-auto mb-1" />
                    <span className="text-xs font-medium block">{opt.label}</span>
                  </button>
                ))}
              </div>
              {providerStatus === 'pending' && providerDetails && (
                <div className="mt-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-700 flex items-center justify-between" data-testid="provider-summary">
                  <span className="truncate">
                    <User className="w-3 h-3 inline mr-1" />
                    {providerDetails.name || providerDetails.phone || providerDetails.email}
                    {providerDetails.matched_user_id && <Badge className="ml-2 bg-green-100 text-green-700 text-[10px] border-0">linked</Badge>}
                  </span>
                  <button onClick={() => setShowAddProviderInline(true)} className="text-gray-500 hover:text-gray-900">
                    <Edit3 className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* My Providers list expands directly below the Provider row when toggled */}
              <div>
                {myProvidersOpen && (
                  <div className="mt-2 space-y-1 max-h-32 overflow-y-auto" data-testid="my-providers-list">
                    {myProviders.length === 0 ? (
                      <p className="text-xs text-gray-400 px-2 py-2">No providers linked yet. Use Add Provider to silently link by phone or email.</p>
                    ) : myProviders.map(p => (
                        <button
                          key={p.provider_user_id}
                          onClick={() => {
                            setProviderDetails({
                              name: p.name,
                              matched_user_id: p.provider_user_id,
                              phone: null,
                              email: null
                            });
                            setProviderStatus('pending');
                            setMyProvidersOpen(false);
                            toast.success(`${p.name} linked`);
                          }}
                          className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-100 flex items-center gap-2 text-xs"
                          data-testid={`my-provider-${p.provider_user_id}`}
                        >
                          {p.profile_image ? (
                            <img src={p.profile_image} className="w-6 h-6 rounded-full object-cover" alt="" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                              <User className="w-3 h-3 text-gray-400" />
                            </div>
                          )}
                          <span className="font-medium text-gray-800 flex-1 truncate">{p.name}</span>
                          {p.business_name && <span className="text-gray-400 truncate">{p.business_name}</span>}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Preferences + Inspo - Iter 9.50+ R9 - side-by-side in a 2-col
                grid so the user can edit both without scrolling. Each tile
                opens its own popup (PreferencePopupTrigger / InspoPopupTrigger). */}
            {selectedCategory?.id && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="plan-prefs-inspo-grid">
                <div data-testid="plan-preferences-section" className="rounded-xl border border-stone-200 p-3 bg-white">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-3.5 h-3.5 text-purple-500" />
                    <label className="text-xs font-semibold uppercase tracking-wide text-stone-700" data-testid="plan-section-preferences-label">{sectionLabel("section_preferences_label", "Preferences")}</label>
                  </div>
                  <PreferencePopupTrigger
                    categoryId={selectedCategory.id}
                    value={preferenceAnswers}
                    onChange={setPreferenceAnswers}
                  />
                </div>
                <div data-testid="plan-inspo-section" className="rounded-xl border border-stone-200 p-3 bg-white">
                  <div className="flex items-center gap-2 mb-2">
                    <ImageIcon className="w-3.5 h-3.5 text-amber-500" />
                    <label className="text-xs font-semibold uppercase tracking-wide text-stone-700" data-testid="plan-section-inspo-label">{sectionLabel("section_inspo_label", "Inspo")}</label>
                  </div>
                  <InspoPopupTrigger
                    categoryId={selectedCategory?.id}
                    selectedInspo={selectedInspo}
                    userInspo={userInspo}
                    onChange={(urls) => {
                      setSelectedInspo(urls);
                      if (urls.length > 0 && !coverImageUrl) {
                        setCoverImageUrl(urls[0]);
                      }
                    }}
                    sectionLabel={sectionLabel("section_inspo_label", "Inspo")}
                  />
                </div>
              </div>
            )}

            {/* Style/Retail-only: Brand + categorized link (Iter 9.39a). */}
            {(() => {
              const catId = String(selectedCategory?.id || "").replace(/^cat_/, "").toLowerCase();
              if (catId !== "style") return null;
              const STYLE_LINK_CATS = [
                { value: "top", label: "Top" },
                { value: "bottom", label: "Bottom" },
                { value: "dress", label: "Dress" },
                { value: "shoes", label: "Shoes" },
                { value: "accessories", label: "Accessories" },
                { value: "outerwear", label: "Outerwear" },
                { value: "lingerie", label: "Lingerie" },
              ];
              return (
                <div data-testid="style-brand-block" className="rounded-xl border border-gray-200 p-3 bg-gray-50/50 space-y-3">
                  <label className="text-xs font-medium text-gray-700 uppercase tracking-wide block" data-testid="plan-section-brand-link-label">
                    <Tag className="w-3.5 h-3.5 inline mr-1" />
                    {sectionLabel("section_brand_link_label", "Brand & link")}
                    <span className="text-[10px] normal-case text-gray-400 ml-1">{slotText(planModal, "section_brand_link_hint", "(brands come from the master directory)")}</span>
                  </label>
                  <BrandPicker
                    value={selectedBrandId}
                    onChange={(id) => setSelectedBrandId(id)}
                    category="style"
                    testid="plan-style-brand"
                  />
                  <div>
                    <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-0.5 block">{slotText(planModal, "section_link_category_label", "Link category")}</label>
                    <div className="flex flex-wrap gap-1.5">
                      {STYLE_LINK_CATS.map((c) => {
                        const active = styleLinkCategory === c.value;
                        return (
                          <button
                            key={c.value}
                            type="button"
                            onClick={() => setStyleLinkCategory(active ? "" : c.value)}
                            className={`text-[11px] px-2.5 py-1 rounded-full border ${active ? "bg-purple-600 text-white border-purple-600" : "bg-white text-gray-700 border-gray-200 hover:border-purple-300"}`}
                            data-testid={`plan-style-link-cat-${c.value}`}
                          >
                            {c.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Style/Retail-only: Size storage block (Iter 9.28). Inline edit, persists to user defaults on save. */}
            {(() => {
              const catId = String(selectedCategory?.id || "").replace(/^cat_/, "").toLowerCase();
              if (catId !== "style") return null;
              const fieldRow = (key, label, placeholder) => (
                <div key={key} className="flex flex-col">
                  <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-0.5">{label}</label>
                  <Input
                    value={styleSizes[key] || ""}
                    onChange={(e) => setStyleSizes(prev => ({ ...prev, [key]: e.target.value }))}
                    placeholder={placeholder}
                    className="h-8 text-xs"
                    data-testid={`style-size-${key}`}
                  />
                </div>
              );
              return (
                <div data-testid="style-size-storage-block" className="rounded-xl border border-gray-200 p-3 bg-gray-50/50">
                  <label className="text-xs font-medium text-gray-700 uppercase tracking-wide block mb-2" data-testid="plan-section-my-sizes-label">
                    <ShoppingBag className="w-3.5 h-3.5 inline mr-1" />
                    {sectionLabel("section_my_sizes_label", "My sizes")}
                    <span className="text-[10px] normal-case text-gray-400 ml-1">{slotText(planModal, "section_my_sizes_hint", "(stored on your profile, edit anytime)")}</span>
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {fieldRow("top", "Top", "M / 8")}
                    {fieldRow("bottom", "Bottom", "28 / 6")}
                    {fieldRow("dress", "Dress", "S / 4")}
                    {fieldRow("shoe", "Shoe", "8.5")}
                    {fieldRow("bra", "Bra", "34B")}
                  </div>
                  <div className="mt-2">
                    <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-0.5 block">{slotText(planModal, "section_my_sizes_notes_label", "Notes (fit, fabric preferences, etc.)")}</label>
                    <Input
                      value={styleSizes.notes || ""}
                      onChange={(e) => setStyleSizes(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="e.g. prefer high-waist, run small at brand X"
                      className="h-8 text-xs"
                      data-testid="style-size-notes"
                    />
                  </div>
                </div>
              );
            })()}

            {/* Inspo block moved into the Preferences+Inspo grid above (Iter 9.50+ R9). */}

            {/* Link to occasion - mirrors the same trigger pattern. Search picker
                combining Phygital templates + user's own occasions + "create new". */}
            <OccasionPickerInline
              category={selectedCategory?.id}
              userOccasions={userOccasions}
              value={linkedOccasionId}
              onChange={setLinkedOccasionId}
            />

            {/* Budget - Iter 9.50+ R6 - per-service budget + optional products budget */}
            <div className="space-y-2" data-testid="plan-budget-section">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block">Budget (optional)</label>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <p className="text-[10px] text-gray-500">Service ($)</p>
                  <div className="flex items-center gap-1 px-2 py-1.5 border border-gray-200 rounded-lg bg-white">
                    <span className="text-xs text-gray-400">$</span>
                    <input
                      type="number"
                      min="0"
                      value={budgetService}
                      onChange={(e) => setBudgetService(e.target.value)}
                      placeholder="0"
                      className="w-full text-xs focus:outline-none"
                      data-testid="budget-service-input"
                    />
                  </div>
                </div>
                {(providerStatus === "diy" || providerStatus === "tbd") && (
                  <div className="space-y-1">
                    <p className="text-[10px] text-gray-500">Products ($)</p>
                    <div className="flex items-center gap-1 px-2 py-1.5 border border-gray-200 rounded-lg bg-white">
                      <span className="text-xs text-gray-400">$</span>
                      <input
                        type="number"
                        min="0"
                        value={budgetProducts}
                        onChange={(e) => setBudgetProducts(e.target.value)}
                        placeholder="0"
                        className="w-full text-xs focus:outline-none"
                        data-testid="budget-products-input"
                      />
                    </div>
                  </div>
                )}
              </div>
              {providerStatus === "diy" && (
                <p className="text-[10px] text-gray-400">DIY: includes products you&apos;ll buy ahead.</p>
              )}
            </div>

            {/* Location - Iter 9.50+ R13
                Three modes the user toggles between:
                - TBD: city autocomplete (Google Places autocomplete-style),
                       stores "City, State, Country"
                - Provider: read-only inherited address from the selected
                            provider's directory record (or first MyProvider).
                - Travel: full address the host travels to + parking/access notes
                          that get shared with the host on confirm. */}
            <div className="space-y-2" data-testid="plan-location-section">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block">Location</label>
              <div className="flex gap-1.5">
                {[
                  { v: "tbd", label: "TBD - just city" },
                  { v: "provider", label: "At provider" },
                  { v: "travel", label: "Travel to me" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setLocationMode(opt.v)}
                    className={`flex-1 text-[11px] px-2 py-1.5 rounded-lg border transition-colors ${locationMode === opt.v ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"}`}
                    data-testid={`plan-location-mode-${opt.v}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {locationMode === "tbd" && (
                <div className="relative">
                  <MapPin className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-stone-400" />
                  <input
                    value={locationCity}
                    onChange={async (e) => {
                      const q = e.target.value;
                      setLocationCity(q);
                      if (q.length < 2) { setCitySuggestions([]); return; }
                      // Lightweight prefix match against pre-seeded Airtable cities
                      // (no Google Maps key burn for typeahead).
                      try {
                        const r = await fetch(`${API}/cities/search?q=${encodeURIComponent(q)}`);
                        if (r.ok) {
                          const d = await r.json();
                          setCitySuggestions((d.cities || []).slice(0, 6));
                        }
                      } catch { /* offline / network - drop suggestions silently */ }
                    }}
                    placeholder="City, State, Country"
                    className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs"
                    data-testid="plan-location-city-input"
                  />
                  {citySuggestions.length > 0 && (
                    <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-40 overflow-y-auto" data-testid="plan-location-city-suggestions">
                      {citySuggestions.map((c, idx) => (
                        <button
                          key={c.full_label || `${c.label}-${idx}`}
                          type="button"
                          onClick={() => { setLocationCity(c.full_label || c.label); setCitySuggestions([]); }}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-stone-100 flex items-center gap-2"
                          data-testid={`plan-location-city-option-${idx}`}
                        >
                          <MapPin className="w-3 h-3 text-stone-400" />
                          <span>{c.full_label || c.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-gray-400 mt-1">Just the city, not a full address. We&apos;ll narrow it down with your host.</p>
                </div>
              )}
              {locationMode === "provider" && (
                <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2.5" data-testid="plan-location-provider-block">
                  {providerDetails?.address || providerDetails?.directory_address ? (
                    <>
                      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">{providerDetails?.name || "Provider"}</p>
                      <p className="text-xs text-stone-800 leading-snug mt-0.5">{providerDetails?.address || providerDetails?.directory_address}</p>
                      <p className="text-[10px] text-stone-400 mt-1">Pulled from the provider&apos;s directory. They control updates.</p>
                    </>
                  ) : (
                    <p className="text-[11px] text-stone-500">Pick a provider above and their studio address will appear here.</p>
                  )}
                </div>
              )}
              {locationMode === "travel" && (
                <div className="space-y-1.5" data-testid="plan-location-travel-block">
                  <div className="relative">
                    <MapPin className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-stone-400" />
                    <input
                      value={locationAddress}
                      onChange={(e) => setLocationAddress(e.target.value)}
                      placeholder="Full address (street, city, state, ZIP)"
                      className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs"
                      data-testid="plan-location-travel-address"
                    />
                  </div>
                  <textarea
                    value={locationNotes}
                    onChange={(e) => setLocationNotes(e.target.value)}
                    placeholder="Parking, building access, doorman, pet at home, anything they need to know..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs resize-none"
                    data-testid="plan-location-travel-notes"
                  />
                  <p className="text-[10px] text-gray-400">Shared with your host once you confirm. Travel fee may apply.</p>
                </div>
              )}
            </div>
            
            {/* Save + Eye preview - Iter 9.50+ R10
                Save plan remains primary CTA. Eye button (square 44x44) sits
                to its left and opens a Preference Card preview overlay that
                shows everything currently selected (service, time, prefs with
                images, products, inspo, budget) exactly as the host would see
                it on the share-card. */}
            <div className="pt-3 border-t">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPreviewCard(true)}
                  className="h-11 w-11 p-0 border-gray-300 hover:bg-gray-50"
                  data-testid="preview-preference-card-btn"
                  title="Preview preference card"
                >
                  <Eye className="w-4 h-4 text-gray-700" />
                </Button>
                <Button
                  onClick={handleCreate}
                  className="flex-1 h-11 bg-gray-900 hover:bg-gray-800 text-white font-semibold"
                  data-testid="create-experience-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {slotText(planSlotMap, "primary_cta_label", "Save preference card")}
                </Button>
              </div>
            </div>
          </div>
        )}

        <PreferenceCardPreviewModal
          open={showPreviewCard}
          onClose={() => setShowPreviewCard(false)}
          experienceName={experienceName}
          selectedCategory={selectedCategory}
          selectedService={selectedService}
          experienceDate={experienceDate}
          experienceTime={experienceTime}
          slotMode={slotMode}
          secondarySlots={secondarySlots}
          planStatus={planStatus}
          preferenceAnswers={preferenceAnswers}
          selectedInspo={selectedInspo}
          budgetService={budgetService}
          budgetProducts={budgetProducts}
          providerStatus={providerStatus}
          providerDetails={providerDetails}
        />

        <AddProviderInline
          open={showAddProviderInline}
          serviceLabel={selectedCategory?.name || "this service"}
          category={selectedCategory?.id ? String(selectedCategory.id).replace(/^cat_/, "") : ""}
          onClose={() => setShowAddProviderInline(false)}
          onSave={(details) => {
            // Multi-select path: keep first as primary; persist all matched_user_ids on the experience via provider_user_ids field
            if (details.multi && details.providers?.length > 0) {
              const primary = details.providers[0];
              setProviderDetails({ name: primary.name, matched_user_id: primary.matched_user_id, phone: null, email: null });
              setProviderStatus('pending');
              setLinkedProviderIds(details.providers.map(p => p.matched_user_id).filter(Boolean));
              setShowAddProviderInline(false);
              toast.success(`${details.providers.length} provider${details.providers.length === 1 ? "" : "s"} linked`);
              return;
            }
            setProviderDetails(details);
            setProviderStatus('pending');
            setShowAddProviderInline(false);
            toast.success(details.matched_user_id ? "Provider linked silently" : "Provider saved");
          }}
        />
        
        <AddProductInline
          open={productPickerOpen}
          category={selectedCategory?.id ? String(selectedCategory.id).replace(/^cat_/, "") : ""}
          onClose={() => setProductPickerOpen(false)}
          onSelect={(p) => {
            const pid = p.product_id;
            if (!pid) return;
            // Cache the product on masterProducts so it renders with image+name
            if (!masterProducts.find(mp => mp.product_id === pid)) {
              setMasterProducts([...masterProducts, p]);
            }
            if (!linkedProductIds.includes(pid)) {
              setLinkedProductIds([...linkedProductIds, pid]);
            }
            setProductPickerOpen(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

/**
 * PlanEventModal - Plan for an Event with per-service date pickers
 */
export const PlanEventModal = ({
  isOpen,
  onClose,
  onSelect
}) => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [step, setStep] = useState(1);
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [services, setServices] = useState([]);
  const [serviceProviders, setServiceProviders] = useState({});
  const [serviceDates, setServiceDates] = useState({});
  const [expandedServiceDate, setExpandedServiceDate] = useState(null);
  const [providerModalFor, setProviderModalFor] = useState(null);
  const [serviceProviderDetails, setServiceProviderDetails] = useState({});
  // Iter 9.50+ R9 - Full per-service plan storage. When a user clicks "Edit"
  // on a service row, we open the standalone PlanExperienceModal for that
  // category. Whatever they save (service, time, secondary_slots, budget,
  // preferences, inspo) is persisted here, keyed by svcId.
  const [servicePlans, setServicePlans] = useState({});
  const [editingFullPlanFor, setEditingFullPlanFor] = useState(null);
  
  const { serviceCategories, visibleEventPresets, loading: categoriesLoading } = useExperienceCategories();

  const handleEventSelect = (event) => {
    setSelectedEvent(event);
    setEventName(event.name);
    setServices(event.suggestedServices || []);
    setStep(2);
  };

  const toggleService = (serviceId) => {
    setServices(prev => 
      prev.includes(serviceId) ? prev.filter(s => s !== serviceId) : [...prev, serviceId]
    );
  };

  const handleCreate = () => {
    if (!selectedEvent || services.length === 0) return;
    
    onSelect?.({
      event_type: selectedEvent.id,
      name: eventName || `My ${selectedEvent.name}`,
      date: eventDate?.toISOString() || null,
      services: services,
      service_providers: serviceProviders,
      service_dates: serviceDates,
      service_provider_details: serviceProviderDetails,
      type: "event"
    });
    
    resetAndClose();
  };

  const resetAndClose = () => {
    setSelectedEvent(null);
    setStep(1);
    setEventName("");
    setEventDate(null);
    setShowDatePicker(false);
    setEditingName(false);
    setServices([]);
    setServiceProviders({});
    setServiceDates({});
    setExpandedServiceDate(null);
    setProviderModalFor(null);
    setServiceProviderDetails({});
    onClose?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === 1 ? "What's the occasion?" : `Plan Your ${selectedEvent?.name} Experience`}
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Event Type Selection - simple white cards with gray icons */}
        {step === 1 && (
          <div className="mt-2" data-testid="event-type-grid">
            {categoriesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {visibleEventPresets.map((preset) => {
                  const IconComponent = getIconComponent(preset.icon);
                  return (
                    <button
                      key={preset.id}
                      onClick={() => handleEventSelect(preset)}
                      className="px-4 py-5 rounded-xl border border-gray-200 bg-white text-left hover:border-gray-900 hover:shadow-sm transition-all flex items-center gap-3"
                      data-testid={`event-${preset.id}`}
                    >
                      <IconComponent className="w-5 h-5 flex-shrink-0 text-gray-500" strokeWidth={1.5} />
                      <span className="font-medium text-gray-800 text-sm">{preset.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Event Config */}
        {step === 2 && selectedEvent && (
          <div className="space-y-4 mt-2" data-testid="event-config">
            <button onClick={() => { setStep(1); setSelectedEvent(null); }} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            
            {/* Event name */}
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-200">
              <PartyPopper className="w-5 h-5 text-gray-700" />
              {editingName ? (
                <Input
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  onBlur={() => setEditingName(false)}
                  onKeyDown={(e) => e.key === 'Enter' && setEditingName(false)}
                  autoFocus
                  className="h-7 text-sm border-0 bg-transparent p-0 focus-visible:ring-0"
                />
              ) : (
                <button onClick={() => setEditingName(true)} className="flex items-center gap-1.5 text-sm font-medium">
                  {eventName || selectedEvent.name}
                  <Edit3 className="w-3 h-3 text-gray-400" />
                </button>
              )}
            </div>
            
            {/* Event Date */}
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-2">Event Date</label>
              <div className="flex gap-2 mb-2">
                <button
                  onClick={() => { setEventDate(null); setShowDatePicker(false); }}
                  className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
                    !eventDate && !showDatePicker ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid="event-date-tbd-btn"
                >
                  <Clock className="w-3.5 h-3.5 inline mr-1.5" />
                  Date TBD
                </button>
                <button
                  onClick={() => setShowDatePicker(true)}
                  className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
                    showDatePicker || eventDate ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid="event-date-pick-btn"
                >
                  <CalendarIcon className="w-3.5 h-3.5 inline mr-1.5" />
                  {eventDate ? eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Pick a date'}
                </button>
              </div>
              {showDatePicker && (
                <Calendar
                  mode="single"
                  selected={eventDate}
                  onSelect={(date) => setEventDate(date)}
                  className="rounded-lg border"
                />
              )}
            </div>
            
            {/* Services to link */}
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-2">Link Services</label>
              <div className="grid grid-cols-3 gap-2">
                {serviceCategories.filter(isV1Category).map((cat) => {
                  const IconComponent = getIconComponent(cat.icon, cat.id);
                  const isSelected = services.includes(cat.id);
                  const solid = getCategoryHex(cat.id, cat.dark_hex || cat.color);
                  return (
                    <button
                      key={cat.id}
                      onClick={() => toggleService(cat.id)}
                      className="p-2.5 rounded-xl border-2 text-center transition-all"
                      style={isSelected
                        ? { borderColor: solid, backgroundColor: hexToSoftBg(solid) }
                        : { borderColor: "#e5e7eb" }}
                      data-testid={`toggle-service-${cat.id}`}
                    >
                      <div className="relative inline-block">
                        <IconComponent className="w-5 h-5 mx-auto mb-1" style={{ color: isSelected ? solid : "#9ca3af" }} />
                        {isSelected && (
                          <div className="absolute -top-1 -right-2 w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: solid }}>
                            <Check className="w-2.5 h-2.5 text-white" />
                          </div>
                        )}
                      </div>
                      <span className="text-xs font-medium block" style={{ color: isSelected ? solid : "#6b7280" }}>{v1Label(cat)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Per-service date + provider */}
            {services.length > 0 && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-2">Service Details</label>
                <div className="space-y-2">
                  {services.map(svcId => {
                    const cat = serviceCategories.find(c => c.id === svcId);
                    if (!cat) return null;
                    const IconComponent = getIconComponent(cat.icon, cat.id);
                    const solid = getCategoryHex(cat.id, cat.dark_hex || cat.color);
                    const provider = serviceProviders[svcId] || 'tbd';
                    const svcDate = serviceDates[svcId];
                    const isExpanded = expandedServiceDate === svcId;
                    
                    return (
                      <div key={svcId} className="rounded-xl border overflow-hidden" style={{ backgroundColor: hexToSoftBg(solid), borderColor: solid + "55" }}>
                        <div className="p-2.5 flex items-center gap-2">
                          <IconComponent className="w-4 h-4 flex-shrink-0" style={{ color: solid }} />
                          <span className="font-medium text-xs flex-1">
                            {cat.name}
                            {servicePlans[svcId]?.service_name && (
                              <span className="ml-1.5 text-[10px] text-gray-500 font-normal">- {servicePlans[svcId].service_name}</span>
                            )}
                          </span>

                          {/* Iter 9.50+ R9 - Edit pencil opens FULL Plan Experience
                              modal for this category (service picker, date+time,
                              secondaries, preferences, inspo, budget). */}
                          <button
                            onClick={() => setEditingFullPlanFor(svcId)}
                            className="w-7 h-7 rounded-full bg-white/80 hover:bg-white flex items-center justify-center"
                            title="Edit full service plan"
                            data-testid={`event-service-edit-${svcId}`}
                          >
                            <Edit3 className="w-3 h-3 text-gray-600" />
                          </button>

                          {/* Date pill */}
                          <button 
                            onClick={() => setExpandedServiceDate(isExpanded ? null : svcId)}
                            className="px-2 py-0.5 rounded-full bg-white/80 text-[10px] font-medium text-gray-600 hover:bg-white flex items-center gap-1"
                          >
                            <CalendarIcon className="w-2.5 h-2.5" />
                            {svcDate 
                              ? new Date(svcDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                              : 'TBD'
                            }
                            <ChevronDown className={`w-2.5 h-2.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>
                          
                          {/* Provider pills */}
                          <div className="flex gap-1">
                            {[
                              { id: 'tbd', label: 'TBD', testid: 'tbd' },
                              { id: 'diy', label: 'DIY', testid: 'diy' },
                              { id: 'pending', label: 'Add Provider', testid: 'add_provider' }
                            ].map(opt => (
                              <button
                                key={opt.id}
                                onClick={() => {
                                  setServiceProviders({ ...serviceProviders, [svcId]: opt.id });
                                  if (opt.id === 'pending') setProviderModalFor(svcId);
                                }}
                                className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-all ${
                                  provider === opt.id 
                                    ? 'bg-gray-900 text-white' 
                                    : 'bg-white/70 text-gray-500 hover:bg-white'
                                }`}
                                data-testid={`event-provider-${svcId}-${opt.testid}`}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        {/* Inline date picker */}
                        {isExpanded && (
                          <div className="px-2.5 pb-2.5 border-t border-white/50">
                            <div className="flex gap-2 my-2">
                              <button
                                onClick={() => { setServiceDates({ ...serviceDates, [svcId]: null }); setExpandedServiceDate(null); }}
                                className={`flex-1 py-1.5 px-2 rounded text-xs font-medium transition-all ${
                                  !svcDate ? 'bg-gray-900 text-white' : 'bg-white/70 text-gray-600'
                                }`}
                              >
                                TBD
                              </button>
                              {eventDate && (
                                <button
                                  onClick={() => { setServiceDates({ ...serviceDates, [svcId]: eventDate.toISOString() }); setExpandedServiceDate(null); }}
                                  className="flex-1 py-1.5 px-2 rounded text-xs font-medium bg-white/70 text-gray-600 hover:bg-white"
                                >
                                  Same as event
                                </button>
                              )}
                            </div>
                            <Calendar
                              mode="single"
                              selected={svcDate ? new Date(svcDate) : undefined}
                              onSelect={(date) => {
                                setServiceDates({ ...serviceDates, [svcId]: date?.toISOString() || null });
                                setExpandedServiceDate(null);
                              }}
                              className="rounded-lg border bg-white"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Save */}
            <div className="pt-3 border-t">
              <Button 
                onClick={handleCreate}
                disabled={services.length === 0}
                className="w-full h-11 bg-gray-900 hover:bg-gray-800 text-white font-semibold disabled:bg-gray-300 disabled:text-gray-500"
                data-testid="create-event-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                Save {services.length > 0 ? `${services.length} Experience${services.length > 1 ? 's' : ''}` : 'Event'}
              </Button>
            </div>
          </div>
        )}

        <AddProviderInline
          open={!!providerModalFor}
          serviceLabel={(serviceCategories.find(c => c.id === providerModalFor)?.name) || "this service"}
          onClose={() => setProviderModalFor(null)}
          onSave={(details) => {
            if (providerModalFor) {
              setServiceProviderDetails({ ...serviceProviderDetails, [providerModalFor]: details });
              setServiceProviders({ ...serviceProviders, [providerModalFor]: 'pending' });
              toast.success(details.matched_user_id ? "Provider linked" : "Provider saved");
            }
            setProviderModalFor(null);
          }}
        />

        {/* Iter 9.50+ R9 - nested full Plan Experience modal for editing a
            single service within the occasion. */}
        <PlanExperienceModal
          isOpen={!!editingFullPlanFor}
          onClose={() => setEditingFullPlanFor(null)}
          prefilledCategoryId={editingFullPlanFor}
          prefilledExperienceDate={editingFullPlanFor && serviceDates[editingFullPlanFor] ? new Date(serviceDates[editingFullPlanFor]) : eventDate}
          prefilledOccasionId={selectedEvent?.id}
          existingPlan={editingFullPlanFor ? servicePlans[editingFullPlanFor] : null}
          onSelect={(plan) => {
            if (editingFullPlanFor) {
              setServicePlans({ ...servicePlans, [editingFullPlanFor]: plan });
              if (plan.date) setServiceDates({ ...serviceDates, [editingFullPlanFor]: plan.date });
              if (plan.provider_user_id) {
                setServiceProviders({ ...serviceProviders, [editingFullPlanFor]: 'pending' });
                setServiceProviderDetails({ ...serviceProviderDetails, [editingFullPlanFor]: { name: plan.provider_name, matched_user_id: plan.provider_user_id, email: plan.provider_email, phone: plan.provider_phone } });
              } else if (plan.provider_status) {
                setServiceProviders({ ...serviceProviders, [editingFullPlanFor]: plan.provider_status });
              }
            }
            setEditingFullPlanFor(null);
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default { PlanExperienceModal, PlanEventModal };
